import loadADIInvoices from "@NA/Handlers/InvoiceHistory/ADIInvoiceHistoryHandler";
import updateInvoicePayment from "@NA/Handlers/InvoiceHistory/ADIUpdateInvoicePaymentRecapHandler";
import CrossIcon from "@NA/Icons/CrossIcon";
import ErrorCrossIcon from "@NA/Icons/ErrorCrossIcon";
import ExportIcon from "@NA/Icons/ExportIcon";
import ImportIcon from "@NA/Icons/ImportIcon";
import PrintIcon from "@NA/Icons/PrintIcon";
import SortUpArrow from "@NA/Icons/SortUpArrow";
import WarningIcon from "@NA/Icons/WarningIcon";
import { Invoice, InvoicePaymentModel, ReasonCodes } from "@NA/Services/ADIInvoiceService";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InvoiceDetailPageTypeLink from "@NA/Overrides/Components/InvoiceDetailsPageTypeLink";
import { InvoiceHistoryPageContext } from "@insite/content-library/Pages/InvoiceHistoryPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import { ClickableProps, StyledButton } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";
import invoiceExportHandler from "@NA/Handlers/InvoiceHistory/ADIInvoiceExportHandler";
import RightArrow from "@NA/Icons/RightArrow";
import SortAmountUp from "@NA/Icons/SortAmountUp";
import { ButtonProps } from "@insite/mobius/Button";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Hidden from "@insite/mobius/Hidden";
import { FormFieldStyle } from "@insite/mobius/FormField";
import siteMessage from "@insite/client-framework/SiteMessage";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import ADIInvoiceHistoryImportModal from "./ADIInvoiceHistoryImportModal";
import { CustomSettingCollectionModel } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";

const enum fields {
    showInvoiceNumber = "showInvoiceNumber",
    showInvoiceDate = "showInvoiceDate",
    showTerms = "showTerms",
    showDueDate = "showDueDate",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showStatus = "showStatus",
    showInvoiceTotal = "showInvoiceTotal",
    showCurrentBalance = "showCurrentBalance",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showInvoiceNumber]: boolean;
        [fields.showInvoiceDate]: boolean;
        [fields.showTerms]: boolean;
        [fields.showDueDate]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showStatus]: boolean;
        [fields.showInvoiceTotal]: boolean;
        [fields.showCurrentBalance]: boolean;
    };
}

export interface FileCollection {
    fileName: string;
    fileContentBase64: string;
}

interface PaginationInfo {
    currentPage: number;
    pageSize: number;
    totalItemCount: number;
}

const mapStateToProps = (state: CustomApplicationState) => ({
    invoiceData: state.ADIGlobalData.ADIInvoiceStore.invoiceResults,
    isLoading: state.ADIGlobalData.ADIInvoiceStore.isLoading,
    multipleLocationInvocies: state.ADIGlobalData.ADIInvoiceStore.allLocationsInvoice,
    getInvoiceParams: state.ADIGlobalData.ADIInvoiceStore.loadInvoiceParameters,
    invoiceRecapPage: getPageLinkByPageType(state, "InvoicePaymentRecap")?.url,
    billTo: getBillToState(state, state.context.session.billToId).value,
    settingsCollection: getSettingsCollection(state) as unknown as CustomSettingCollectionModel,
    isReturnToInvoice: state.ADIGlobalData.ADIInvoiceStore.isRetrunToInvocie,
    getCustomerCreditStatus: state.ADIGlobalData.PricingStatusStore,
});

const mapDispatchToProps = {
    updateSearchFields,
    updateInvoicePayment,
    loadADIInvoices,
    invoiceExportHandler,
};

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceHistoryTableStyles {
    container?: GridContainerProps;
    headerClickables?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    GridItemHeader?: GridItemProps;
    GridItemHeaderTab?: GridItemProps;
    exportContainer?: GridContainerProps;
    exportGridItem?: GridItemProps;
    pageresults?: TypographyProps;
    exportImportButton?: InjectableCss;
    Icons?: InjectableCss;
    GridItemInvoices?: GridItemProps;
    singleInvoiceGridItem?: GridItemProps;
    searchResult?: InjectableCss;
    selectAll?: CheckboxProps;
    dateColumn?: InjectableCss;
    tabDateColumn?: InjectableCss;
    typeColumn?: InjectableCss;
    tabTypeColumn?: InjectableCss;
    invoiceColumn?: InjectableCss;
    tabInvoiceColumn?: InjectableCss;
    poColumn?: InjectableCss;
    tabPoColumn?: InjectableCss;
    tabOrderColumn?: InjectableCss;
    dueDateColumn?: InjectableCss;
    tabDueDateColumn?: InjectableCss;
    dpdColumn?: InjectableCss;
    currentBalColumn?: InjectableCss;
    tabCurrentBalColumn?: InjectableCss;
    actionColumn?: InjectableCss;
    dateColumnBody?: InjectableCss;
    tabDateColumnBody?: InjectableCss;
    invoiceTypeBody?: InjectableCss;
    tabInvoiceTypeBody?: InjectableCss;
    invoiceLinkBody?: InjectableCss;
    tabInvoiceLinkBody?: InjectableCss;
    printButton?: LinkPresentationProps;
    poColumnBody?: InjectableCss;
    tabPoColumnBody?: InjectableCss;
    dueDateColumnBody?: InjectableCss;
    tabDueDateColumnBody?: InjectableCss;
    dpdColumnBody?: InjectableCss;
    currentBalColumnBody?: InjectableCss;
    tabCurrentBalColumnBody?: InjectableCss;
    actionColumnBody?: InjectableCss;
    priceChangeContainer?: InjectableCss;
    mtPriceChangeContainer?: InjectableCss;
    dropdownContainer?: InjectableCss;
    mtDropdownContainer?: InjectableCss;
    dropdownValue?: InjectableCss;
    dropdownList?: InjectableCss;
    dropdownValues?: InjectableCss;
    dropdownLabel?: InjectableCss;
    icon?: TypographyProps;
    mtIcon?: TypographyProps;
    paymentTotal?: InjectableCss;
    payTotal?: TypographyProps;
    FilterType?: TypographyProps;
    FilterDPD?: TypographyProps;
    tabFilterType?: TypographyProps;
    FilterTypeheading?: InjectableCss;
    FilterTypeContainer?: InjectableCss;
    FilterDPDContainer?: InjectableCss;
    TypeFilterContainer?: InjectableCss;
    tabs?: InjectableCss;
    noInvoicesContainer?: InjectableCss;
    noInvoicesContent?: InjectableCss;
    paymentInfoContainer?: InjectableCss;
    mtPaymentInfoContainer?: InjectableCss;
    mtPaymentInfoContainerInner?: InjectableCss;
    paymentTitle?: InjectableCss;
    mtPaymentTitle?: InjectableCss;
    paymentBtn?: ButtonPresentationProps;
    mtPaymentBtn?: ButtonPresentationProps;
    warningInfoContainer?: InjectableCss;
    warningInfoIcon?: InjectableCss;
    warningInfoText?: TypographyProps;
    mtWarningInfoText?: TypographyProps;
    negativeAmountMsg?: TypographyProps;
    selectedInvoiceInfoMsg?: TypographyProps;
    selectedInvoiceMsgContainer?: InjectableCss;
    actionsClose?: InjectableCss;
    toolTip?: InjectableCss;
    mtPageresults?: TypographyProps;
    mtSingleInvoiceGridItem?: GridContainerProps;
    tabSingleInvoiceGridItem?: GridContainerProps;
    mtPrintButton?: LinkPresentationProps;
    select?: SelectPresentationProps;
    selectFilter?: SelectPresentationProps;
    searchResultGridItem?: GridItemProps;
    pageresultsGridItem?: GridItemProps;
    mtPageresultsGridItem?: GridItemProps;
    exportImportButtonGridItem?: GridItemProps;
    selectGridItem?: GridItemProps;
    mtSelectGridItem?: GridItemProps;
    sortBtn?: ButtonProps;
    mtGridItem?: GridItemProps;
    tabGridItem?: GridItemProps;
    tabPrint?: InjectableCss;
    mtDateColumnBody?: InjectableCss;
    mtInvoiceViewDetailsBody?: InjectableCss;
    mtInvoiceLinkBody?: InjectableCss;
    mtPoColumnBody?: InjectableCss;
    mtDueDateColumnBody?: InjectableCss;
    mtDpdColumnBody?: InjectableCss;
    mtCurrentBalColumnBody?: InjectableCss;
    mtActionColumnBody?: InjectableCss;
    tabActionColumnBody?: InjectableCss;
    mobilecurrentBalColumnBody?: InjectableCss;
    tabbularCurrentBalColumnBody?: InjectableCss;
    mtPaymentTotal?: InjectableCss;
    checkboxImg?: LazyImageProps;
    invoiceNumberNonLink?: TypographyProps;
}

export const tableStyles: InvoiceHistoryTableStyles = {
    container: {
        css: css`
            overflow: visible;
            @media only screen and (min-width: 768px) and (max-width: 1080px) {
                max-width: 100%;
            }
            & .disabled {
                pointer-events: none;
                opacity: 0.55 !important;
            }
            .DESC.viewalllocationinvoice,
            .ASC.viewalllocationinvoiceasc {
                //  padding: 10px 0px !important;
                padding: 10px 0px 0px 0px;
                .viewinvoicedatecheckbox {
                    // padding-left: 20px;
                    // padding-right: 0;
                    // display: flex;
                    flex-basis: 3.6111111111%;
                    width: 3.6111111111%;
                    padding-left: 20px;
                    padding-right: 0;
                    display: flex;
                    align-items: center;
                    @media only screen and (min-width: 768px) and (max-width: 1080px) {
                        padding-right: 29px;
                    }
                    span[role="checkbox"] {
                        height: 18px;
                        width: 18px;
                        min-width: 18px !important;
                        &:focus {
                            outline: none !important;
                            box-shadow: none !important;
                        }
                    }
                }
                .viewinvoicedate {
                    padding-left: 25px !important;
                    @media only screen and (min-width: 768px) and (max-width: 1080px) {
                        padding-left: 30px !important;
                    }
                    padding-right: 0;
                    justify-content: flex-start;
                    display: flex;
                    svg {
                        left: 9px;
                        @media only screen and (min-width: 768px) and (max-width: 1080px) {
                            left: 11px;
                        }
                    }
                }
                .viewinvoicetype {
                    padding-left: 25px !important;
                    justify-content: flex-start;
                    display: flex;
                    padding-right: 30px;
                    .typ {
                        span {
                            right: auto;
                            @media only screen and (min-width: 768px) and (max-width: 1080px) {
                                right: 70px;
                            }
                        }
                    }
                    svg {
                        left: 10px;
                    }
                }
                .viewinvoiceheader {
                    padding-left: 40px;
                    position: relative;
                    justify-content: flex-start;
                    display: flex;
                    svg {
                        left: 25px;
                    }
                }
                .viewinvoicepo {
                    display: flex;
                    padding-right: 30px;
                    padding-left: 30px;
                    justify-content: flex-start;
                    svg {
                        left: 12px;
                    }
                }
                .viewinvoiceduedate {
                    display: flex;
                    padding-left: 30px;
                    svg {
                        left: 10px;
                    }
                }
                .viewinvoicebalance {
                    padding-right: 40px;
                    padding-right: 25px;
                    display: flex;
                    justify-content: flex-end;
                    svg {
                        left: 37px;
                        @media only screen and (min-width: 768px) and (max-width: 1080px) {
                            left: -15px;
                        }
                    }
                }
                .viewinvoicedpd {
                    justify-content: flex-start;
                    padding-left: 26px;
                    display: flex;
                    svg {
                        left: 10px;
                    }
                    .typ span {
                        right: auto;
                        @media only screen and (min-width: 768px) and (max-width: 1080px) {
                            right: 77px;
                        }
                    }
                }
            }
            .viewalllocationinvoiceinside {
                .non-selected {
                    padding: 15px 0px !important;
                }
                .selected {
                    padding: 5px 10px 0px 0px;
                    .viewinvoicecontentbalance {
                        padding-right: 25px;
                        input {
                            padding: 9px 8px;
                            -moz-appearance: textfield;
                        }
                        input::-webkit-outer-spin-button,
                        input::-webkit-inner-spin-button {
                            -webkit-appearance: none;
                            margin: 0;
                        }
                    }
                }
                .viewinvoicecontentcheckbox {
                    // padding-left: 20px;
                    // padding-right: 0;
                    // display: flex;
                    // align-items: center;
                    flex-basis: 3.6111111111%;
                    width: 3.6111111111%;
                    padding-left: 20px;
                    padding-right: 0;
                    display: flex;
                    align-items: center;
                    span[role="checkbox"] {
                        height: 18px;
                        width: 18px;
                        min-width: 18px !important;

                        &:focus {
                            outline: none !important;
                            box-shadow: none !important;
                        }
                    }
                }
                .viewinvoicecontentdotaddeddate {
                    display: flex;
                    flex-flow: row nowrap;
                    flex-grow: 1;
                    padding-left: 25px !important;
                }
                .viewinvoicecontentinvoicetype {
                    padding-left: 25px !important;
                    justify-content: flex-start;
                    display: flex;
                }
                .viewinvoicecontentinvoicenumberprint {
                    display: flex;

                    justify-content: center;
                    padding-left: 20px;
                }
                .viewinvoicecontentpo {
                    display: flex;
                    padding-left: 30px;
                    text-align: left;
                    line-height: normal;
                }
                .viewinvoicecontentduedate {
                    display: flex;
                    padding-left: 30px;
                }
                .viewinvoicecontentbalance {
                    padding-right: 34px;
                    justify-content: flex-end;
                    display: flex;
                }
                .viewinvoicecontentdpd {
                    display: flex;
                    padding-left: 26px;
                }
            }
        `,
        offsetCss: css`
            margin: auto;
            flex-basis: 100%;
            max-width: 100%;
            align-items: flex-start;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    GridItemHeader: {
        css: css`
            max-width: 100%;
            flex-basis: 100%;
            background-color: ${getColor("common.background")};
            text-transform: capitalize;
            text-decoration: none !important;
            font-size: 16px;
            font-weight: bold !important;
            align-items: center;
            padding: 10px 15px;

            .sort-active {
                background-color: ${ADIColors.gray6};
                padding: 4px 0;

                .typ span {
                    top: 10px;
                }

                span {
                    text-decoration: underline;
                }

                svg {
                    display: block;
                }
            }

            &.DESC svg {
                transform: rotate(180deg);
            }

            &.DESC span[role="checkbox"] svg {
                transform: rotate(0deg);
            }
        `,
    },
    GridItemHeaderTab: {
        css: css`
            max-width: 100%;
            flex-basis: 100%;
            background-color: ${getColor("common.background")};
            text-transform: capitalize;
            text-decoration: none !important;
            font-size: 16px;
            font-weight: bold !important;
            align-items: center;
            padding: 10px 15px;
            justify-content: space-between;
            .sort-active {
                background-color: ${ADIColors.gray6};
                padding: 4px 0;

                .typ span {
                    top: 10px;
                }

                span {
                    text-decoration: underline;
                }

                svg {
                    display: block;
                }
            }

            &.DESC svg {
                transform: rotate(180deg);
            }

            &.DESC span[role="checkbox"] svg {
                transform: rotate(0deg);
            }
        `,
    },
    exportContainer: {
        css: css`
            background-color: ${ADIColors.white};
            margin: 24px 0 20px;
            @media only screen and (min-width: 768px) and (max-width: 1080px) {
                max-width: 100%;
            }
            > ${GridOffset} {
                width: 100%;
                margin: auto;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0px 0 20px;
                        background-color: transparent;
                    `,
                    css`
                        margin: 24px 0 20px;
                        background-color: ${ADIColors.white};
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    searchResultGridItem: {
        css: css`
            flex-basis: 50%;
            max-width: 50%;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-basis: 65%;
                            max-width: 65%;
                        `,
                        css`
                            flex-basis: 65%;
                            max-width: 65%;
                        `,

                        null,
                    ],
                    "max",
                )}
        `,
    },
    pageresultsGridItem: {
        css: css`
            flex-basis: 15%;
            max-width: 15%;
        `,
    },
    mtPageresultsGridItem: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            padding: 15px 0;
        `,
    },
    exportImportButtonGridItem: {
        css: css`
            flex-basis: 15%;
            max-width: 15%;
        `,
    },
    selectGridItem: {
        css: css`
            flex-basis: 50%;
            max-width: 50%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-basis: 35%;
                            max-width: 35%;
                        `,
                        css`
                            flex-basis: 35%;
                            max-width: 35%;
                        `,

                        null,
                    ],
                    "max",
                )}
            > ${FormFieldStyle} {
                justify-content: flex-end;
            }
        `,
    },
    mtSelectGridItem: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            padding: 0;
        `,
    },
    pageresults: {
        css: css`
            font-size: 14px;
            margin-top: 4px;
            border-right: 1px solid ${ADIColors.gray3};
            padding-right: 8px;
            flex-basis: 100%;
            max-width: fit-content;
        `,
    },
    mtPageresults: {
        css: css`
            font-size: 14px;
            margin-top: 4px;
            padding-right: 8px;
            flex-basis: 100%;
            max-width: 100%;
        `,
    },
    exportImportButton: {
        css: css`
            background: transparent;
            color: ${getColor("primary")};
            font-size: 14px;
            font-weight: bold;
            font-style: normal;
            line-height: normal;
            outline: none;
            border: none;
            display: flex;
            align-items: center;
            padding: 9px 10px;
            margin-left: 4px;
            &:hover {
                text-decoration: underline;
                background: transparent;
            }
            &:disabled {
                background: transparent;
                color: ${getColor("primary")};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    Icons: {
        css: css`
            height: 17px;
            width: 17px;
            margin: 1px 6px 1px 0;
            > svg {
                width: 17px;
                height: 17px;
            }
        `,
    },
    GridItemInvoices: {
        css: css`
            flex-direction: column;
            flex-basis: 100%;
            max-width: 100%;
            padding: 0px;
            margin: 12px 0 !important;
        `,
    },
    singleInvoiceGridItem: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            width: 100%;
            display: flex;
            justify-content: space-between;
            padding: 15px;
            background-color: ${getColor("common.background")};
            margin-bottom: 12px !important;
            align-items: center;

            * {
                font-size: ${getProp("theme.typography.headerTertiary.size")};
                font-weight: 400;
                align-items: center;
            }

            &.selected {
                box-shadow: 0 2px 4px 2px rgba(102, 102, 102, 0.35);
                padding: 5px 15px;
                * {
                    font-weight: bold;
                }
            }
        `,
    },
    mtSingleInvoiceGridItem: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding: 15px;
            background-color: ${getColor("common.background")};
            margin-bottom: 12px !important;
        `,
    },
    tabSingleInvoiceGridItem: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            width: 100%;
            display: flex;
            justify-content: space-between;
            padding: 15px;
            background-color: ${getColor("common.background")};
        `,
    },
    searchResult: {
        css: css`
            font-size: 20px;
            font-weight: 600;
            line-height: 1.25;
            color: ${ADIColors.darkBlue1};
            margin: 0 12px 0 0;
            flex-basis: 100%;
            max-width: fit-content;
        `,
    },
    selectAll: {
        css: css`
            flex-basis: 3.6111111111%;
            width: 3.6111111111%;
            text-align: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    dateColumn: {
        css: css`
            flex-basis: 9.4444444444% !important;
            width: 9.4444444444%;
            text-align: center;
            position: relative;
            svg {
                left: 25px;
                margin-top: 4px;
                display: none;
                position: absolute;
            }
            &:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    tabDateColumn: {
        css: css`
            flex-basis: 9.4444444444% !important;
            width: 9.4444444444%;
            text-align: center;
            position: relative;
            svg {
                left: 9px;
                margin-top: 4px;
                display: none;
                position: absolute;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                left: 5px;
                            `,
                    ],
                    "max",
                );
            }}
            }
            &:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    typeColumn: {
        css: css`
            flex-basis: 9.7222222222%;
            width: 9.7222222222%;
            text-align: center;
            position: relative;
            overflow: visible;
            svg {
                left: 25px;
                margin-top: 4px;
                display: none;
                position: absolute;
            }
            .typ:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    tabTypeColumn: {
        css: css`
            flex-basis: 9.7222222222%;
            width: 9.7222222222%;
            text-align: center;
            position: relative;
            overflow: visible;
            svg {
                left: 15px;
                margin-top: 4px;
                display: none;
                position: absolute;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                left: 4px;
                            `,
                    ],
                    "max",
                );
            }}
            }
            .typ:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    invoiceColumn: {
        css: css`
            flex-basis: 12.2222222222%;
            width: 12.2222222222%;
            text-align: center;
            position: relative;
            svg {
                left: 35px;
                margin-top: 4px;
                display: none;
                position: absolute;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                left: 5px;
                            `,
                    ],
                    "max",
                );
            }}
            }
            &:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    tabInvoiceColumn: {
        css: css`
            flex-basis: 12.2222222222%;
            width: 12.2222222222%;
            text-align: center;
            position: relative;
            svg {
                left: 20px;
                margin-top: 4px;
                display: none;
                position: absolute;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                left: 5px;
                            `,
                    ],
                    "max",
                );
            }}
            }
            &:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    poColumn: {
        css: css`
            flex-basis: 14.1666666667%;
            width: 14.1666666667%;
            text-align: center;
            position: relative;
            svg {
                left: 42px;
                margin-top: 4px;
                display: none;
                position: absolute;
                ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            left: 22px;
                        `,
                ]);
            }}
            }
            &:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    tabPoColumn: {
        css: css`
            flex-basis: 14.1666666667%;
            width: 14.1666666667%;
            text-align: center;
            position: relative;
            svg {
                left: 38px;
                margin-top: 4px;
                display: none;
                position: absolute;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                left: 18px;
                            `,
                    ],
                    "max",
                );
            }}
            }
            &:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    tabOrderColumn: {
        css: css`
            flex-basis: 14.1666666667%;
            width: 14.1666666667%;
            text-align: center;
            position: relative;
            svg {
                left: 28px;
                margin-top: 4px;
                display: none;
                position: absolute;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                left: 8px;
                            `,
                    ],
                    "max",
                );
            }}
            }
            &:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    dueDateColumn: {
        css: css`
            flex-basis: 11.8055555556%;
            width: 11.8055555556%;
            text-align: center;
            position: relative;
            svg {
                left: 25px;
                margin-top: 4px;
                display: none;
                position: absolute;
            }
            &:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    tabDueDateColumn: {
        css: css`
            flex-basis: 11.8055555556%;
            width: 11.8055555556%;
            text-align: center;
            position: relative;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 14%;
                            flex-basis: 14%;
                        `,
                    ],
                    "max",
                );
            }}
            svg {
                left: 75px;
                margin-top: 4px;
                display: none;
                position: absolute;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            left: 9px;
                        `,
                    null,
                    null,
                ])}
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                left: 0px;
                            `,
                    ],
                    "max",
                );
            }}
            }
            .typ:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    dpdColumn: {
        css: css`
            flex-basis: 10.2777777778%;
            width: 10.2777777778%;
            text-align: center;
            position: relative;
            svg {
                left: 25px;
                margin-top: 4px;
                display: none;
                position: absolute;
            }
            .typ:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    currentBalColumn: {
        css: css`
            flex-basis: 11.1111111111%;
            width: 11.1111111111%;
            text-align: center;
            position: relative;
            svg {
                left: 25px;
                margin-top: 4px;
                display: none;
                position: absolute;
            }
            &:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    tabCurrentBalColumn: {
        css: css`
            flex-basis: 11.1111111111%;
            width: 11.1111111111%;
            text-align: right;
            position: relative;
            svg {
                left: 38px;
                margin-top: 4px;
                display: none;
                position: absolute;
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                left: 9px;
                            `,
                    ],
                    "max",
                );
            }}
            }
            &:hover {
                color: ${ADIColors.blue5};
                cursor: pointer;
                svg {
                    display: block;
                }
            }
        `,
    },
    actionColumn: {
        css: css`
            flex-basis: 17.6388888889%;
            width: 17.6388888889%;
            text-align: center;
            border-left: 2px solid ${ADIColors.white1};
            position: relative;

            svg {
                right: 0;
                margin-top: 4px;
                width: 15px;
                position: absolute;
                top: 0;
                cursor: pointer;
            }
        `,
    },
    dateColumnBody: {
        css: css`
            flex-basis: 9.4444444444% !important;
            width: 9.4444444444%;
            text-align: center;
        `,
    },
    tabDateColumnBody: {
        css: css`
            flex-basis: 13.4444444444% !important;
            width: 13.4444444444%;
            text-align: center;
            font-size: 14px;
        `,
    },
    invoiceTypeBody: {
        css: css`
            flex-basis: 9.7222222222%;
            width: 9.7222222222%;
            display: flex;
            justify-content: center;
            > span {
                color: ${ADIColors.blue7};
                padding: 5px 8px 4px 8px;
                border-radius: 8px;
                border: solid 1px ${ADIColors.blue7};
                line-height: 1;
                display: flex;
                align-items: center;
                min-height: 26px;
                height: auto;
                text-align: center;
                justify-content: center;
                width: max-content;

                &.creditType {
                    color: ${ADIColors.lightGreen1};
                    border: solid 1px ${ADIColors.lightGreen1};
                }
            }
        `,
    },
    tabInvoiceTypeBody: {
        css: css`
            flex-basis: 7.7222222222%;
            width: 7.7222222222%;
            display: flex;
            justify-content: center;
            > span {
                color: ${ADIColors.darkBlack3};
                font-size: 14px;
            }
        `,
    },
    invoiceLinkBody: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-basis: 12.2222222222%;
            width: 12.2222222222%;
            justify-content: center;
            > ${StyledButton} {
                font-weight: bold;
                padding-right: 8px;
            }
        `,
    },
    tabInvoiceLinkBody: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-basis: 13.2222222222%;
            width: 13.2222222222%;
            justify-content: center;
            > ${StyledButton} {
                font-weight: bold;
                padding-right: 8px;
                > ${TypographyStyle} {
                    font-size: 14px;
                }
            }
        `,
    },
    printButton: {
        css: css`
            border-left: 2px solid ${ADIColors.white2};
            padding-left: 8px;
            svg {
                height: 20px;
                width: 20px;
                margin-top: 2px;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    mtPrintButton: {
        css: css`
            padding-left: 8px;
            svg {
                height: 20px;
                width: 20px;
                margin-top: 2px;
                margin-right: 8px;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    poColumnBody: {
        css: css`
            flex-basis: 14.1666666667%;
            width: 14.1666666667%;
            text-align: center;
        `,
    },
    tabPoColumnBody: {
        css: css`
            flex-basis: 14.1666666667%;
            width: 14.1666666667%;
            text-align: center;
            font-size: 14px;
        `,
    },
    dueDateColumnBody: {
        css: css`
            flex-basis: 11.8055555556%;
            width: 11.8055555556%;
            text-align: center;
        `,
    },
    tabDueDateColumnBody: {
        css: css`
            flex-basis: 13.8055555556%;
            width: 13.8055555556%;
            text-align: center;
            font-size: 14px;
        `,
    },
    dpdColumnBody: {
        css: css`
            flex-basis: 10.2777777778%;
            width: 10.2777777778%;
            text-align: center;
        `,
    },
    currentBalColumnBody: {
        css: css`
            flex-basis: 11.1111111111%;
            width: 11.1111111111%;
            text-align: center;
            font-weight: 800 !important;
        `,
    },
    tabCurrentBalColumnBody: {
        css: css`
            flex-basis: 11.1111111111%;
            width: 11.1111111111%;
            text-align: right;
            font-weight: 800 !important;
            font-size: 14px;
        `,
    },
    actionColumnBody: {
        css: css`
            flex-basis: 17.6388888889%;
            width: 17.6388888889%;
            text-align: center;
            border-left: 2px solid ${ADIColors.white1};
            color: ${ADIColors.blue5};
            span {
                font-weight: 800 !important;
                cursor: pointer;
            }
            &.disabled {
                color: ${ADIColors.darkBlack3} !important;
                opacity: 1 !important;
            }
        `,
    },
    actionsClose: {
        css: css`
            > svg {
                margin-right: 18px;
            }
        `,
    },
    priceChangeContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: 10px;
            position: relative;
            margin-right: 20px;
            margin-top: -4px;
            margin-left: auto;
            margin-right: 0;
            max-width: 113px;

            input {
                padding: 0 10px;
                text-align: right;
            }

            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            /* Firefox */
            input[type="number"] {
                -moz-appearance: textfield;
            }

            :hover .tooltip {
                display: block;
            }
        `,
    },
    mtPriceChangeContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: 10px;
            position: relative;
            margin-top: -4px;

            input {
                padding: 0 10px;
                text-align: right;
            }

            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            /* Firefox */
            input[type="number"] {
                -moz-appearance: textfield;
            }

            :hover .tooltip {
                display: block;
            }
        `,
    },
    dropdownLabel: {
        css: css`
            font-weight: 700;
            text-transform: capitalize;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            margin-bottom: 6px;
        `,
    },
    dropdownContainer: {
        css: css`
            position: relative;
            width: 65%;
            min-width: 130px;
            margin: 0 auto;
        `,
    },
    mtDropdownContainer: {
        css: css`
            position: relative;
            width: 80%;
            margin: 0 auto;
        `,
    },
    dropdownValue: {
        css: css`
            width: 100%;
            border: 1px solid;
            border-radius: 6px;
            padding: 9px 9px 9px 15px;
            height: 40px;
            background: ${ADIColors.white};
            border: 1px solid ${ADIColors.gray3};
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            cursor: pointer;
            position: relative;
            z-index: 9;
            display: flex;
            align-items: normal;
            justify-content: space-between;
            text-transform: capitalize;
            color: ${getColor("common.backgroundContrast")};
            line-height: 1.29;
            &:first-child {
                padding: 12px 9px 9px 15px;
            }
            &.open {
                z-index: 11;
            }
        `,
    },
    dropdownList: {
        css: css`
            border-radius: 0 0 7px 7px;
            box-shadow: 1px 2px 14px 4px rgba(57, 57, 58, 0.25);
            background: ${ADIColors.white};
            position: absolute;
            width: 100%;
            top: 90%;
            z-index: 10;
            max-height: 225px;
            overflow: auto;

            ::-webkit-scrollbar {
                width: 6px;
            }

            ::-webkit-scrollbar-track {
                background: ${ADIColors.gray11};
            }

            ::-webkit-scrollbar-thumb {
                background: ${ADIColors.darkBlue2};
            }
        `,
    },
    dropdownValues: {
        css: css`
            min-height: 40px;
            border-bottom: 1px solid ${ADIColors.gray3};
            font-weight: 600;
            padding: 13px 12px;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            cursor: pointer;
            text-align: left;
            font-weight: 400 !important;
            color: ${getColor("common.backgroundContrast")};
            text-transform: capitalize;
            line-height: 1.29;
            &:first-child {
                padding: 16px 12px;
            }
            &:last-child {
                border-bottom: 0;
                border-radius: 0 0 7px 7px;
            }

            &.active,
            &:hover {
                background-color: ${ADIColors.blue1};
                color: ${ADIColors.white};
            }
        `,
    },
    icon: {
        css: css`
            transform: translateY(-1px) scale(0.9);
            svg {
                stroke: ${ADIColors.darkBlue1};
                width: 23px;
                margin-top: 9px;
            }
        `,
    },
    mtIcon: {
        css: css`
            transform: translateY(-1px) scale(0.9);
            svg {
                stroke: ${ADIColors.darkBlue1};
                width: 23px;
                margin-top: 0px;
            }
        `,
    },
    paymentTotal: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
        `,
    },

    FilterType: {
        css: css`
            background-image: url("https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/icon-column-filter@3x.png");
            background-size: contain;
            object-fit: contain;
            width: 16px;
            height: 12px;
            margin-left: 6px;
            display: inline-block;
            position: absolute;
            top: 7px;
            right: 20px;
        `,
    },

    FilterDPD: {
        css: css`
            background-image: url("https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/icon-column-filter@3x.png");
            background-size: contain;
            object-fit: contain;
            width: 16px;
            height: 12px;
            margin-left: 6px;
            display: inline-block;
            position: absolute;
            top: 5px;
            right: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        right: -15px;
                    `,
                    css`
                        right: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tabFilterType: {
        css: css`
            background-image: url("https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/icon-column-filter@3x.png");
            background-size: contain;
            object-fit: contain;
            width: 16px;
            height: 12px;
            margin-left: 6px;
            display: inline-block;
            position: absolute;
            top: 7px;
            right: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        right: -5px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    invoiceNumberNonLink: {
        css: css`
            justify-content: flex-end;
            padding-right: 47px;
        `,
    },
    FilterTypeContainer: {
        css: css`
            box-shadow: 0 1px 12px 0 rgba(0, 0, 0, 0.36);
            padding: 18px 20px 20px;
            border-radius: 6px;
            position: absolute;
            background: ${getColor("common.background")};
            z-index: 9;
            width: 380px;
            right: -63%;
            bottom: -160px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        right: 0px;
                        left: -130px;
                    `,
                    null,
                    null,
                ])}

            &.all-loc-active {
                right: -92%;
                ::after {
                    left: calc(100% - 143px);
                }
            }

            ::after {
                content: " ";
                position: absolute;
                left: 69%;
                top: -15px;
                border-top: none;
                border-right: 15px solid transparent;
                border-left: 15px solid transparent;
                border-bottom: 15px solid #fff;
                border: 12px solid black;
                box-shadow: -3px 3px 3px 0 rgba(0, 0, 0, 0.18);
                border-color: transparent transparent #fff #fff;
                transform: rotate(-225deg);
                top: -10px;
                left: calc(100% - 120px);
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                            left: calc(100% - 200px);
                        `,
                    null,
                    null,
                ])}
            }
        `,
    },
    FilterDPDContainer: {
        css: css`
            box-shadow: 0 1px 12px 0 rgba(0, 0, 0, 0.36);
            padding: 18px 20px 20px;
            border-radius: 6px;
            position: absolute;
            background: ${getColor("common.background")};
            z-index: 9;
            width: 380px;
            right: -57%;
            bottom: -153px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        right: -110px;
                    `,
                    null,
                    null,
                ])}

            &.all-loc-active {
                right: -82%;
                ::after {
                    left: calc(100% - 143px);
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                                left: calc(100% - 85px);
                            `,
                    null,
                    null,
                ])}
                }
            }

            ::after {
                content: " ";
                position: absolute;
                left: 69%;
                top: -15px;
                border-top: none;
                border-right: 15px solid transparent;
                border-left: 15px solid transparent;
                border-bottom: 15px solid #fff;
                border: 12px solid black;
                box-shadow: -3px 3px 3px 0 rgba(0, 0, 0, 0.18);
                border-color: transparent transparent #fff #fff;
                transform: rotate(-225deg);
                top: -10px;
                left: calc(100% - 120px);
            }
        `,
    },
    FilterTypeheading: {
        css: css`
            display: flex;
            justify-content: space-between;
            border-bottom: 1px solid ${ADIColors.gray7};
            padding-bottom: 12px;
            margin-bottom: 19px;
            text-decoration: none !important;
            svg {
                display: inline-block;
                position: static;
                width: 20px;
                cursor: pointer;
            }
            span {
                text-decoration: none !important;
            }
        `,
    },
    TypeFilterContainer: {
        css: css`
            display: flex;
            margin-top: 0px;
            padding: 2px;
            border-radius: 6px;
            border: solid 1px ${ADIColors.gray2};
            background: ${ADIColors.white};
            max-width: 599px;
        `,
    },
    tabs: {
        css: css`
            padding: 6.5px 10px 5.5px 10px;
            border-radius: 6px;
            border: 0;
            background-color: #fff;
            text-align: center;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            line-height: 22px;
            font-weight: normal;
            color: ${ADIColors.darkBlue1};
            display: flex;
            width: calc(100% / 3);
            justify-content: center;
            align-items: center;
            cursor: pointer;
            transition: all 0.1s ease;
            position: relative;
            overflow: visible;
            font-weight: bold;
            &:nth-of-type(2) {
                margin: 0 9px;
            }

            &::after {
                content: "";
                width: 2px;
                height: 22px;
                margin: 6px 0 6px 1px;
                border-radius: 1px;
                background-color: ${ADIColors.gray2};
                position: absolute;
                right: -6px;
                z-index: 10;
            }

            &:last-child::after {
                display: none;
            }

            &.active {
                background-color: ${ADIColors.darkBlue1};
                color: ${ADIColors.white};
            }

            &[aria-disabled="true"] {
                //ADINA-58265-Based on DPD filter, need to disable not only last child
                pointer-events: none;
                opacity: 0.5;
                cursor: default;
            }
            // &:last-child[aria-disabled="true"] {
            //     pointer-events: none;
            //     opacity: 0.5;
            //     cursor: default;
            // }
        `,
    },
    noInvoicesContainer: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 280px;
            text-align: center;
            margin-top: 20px;
            border-top: solid 1px ${ADIColors.gray2};
        `,
    },
    noInvoicesContent: {
        css: css`
            display: flex;
            flex-direction: column;

            span {
                font-size: ${getProp("theme.typography.h4.size")};
                font-weight: bold;
                line-height: 1.6;
                color: ${ADIColors.gray4};
            }
        `,
    },
    paymentInfoContainer: {
        css: css`
            text-align: right;
        `,
    },
    mtPaymentInfoContainer: {
        css: css`
            text-align: right;
            display: flex;
            flex-direction: column;
        `,
    },
    mtPaymentInfoContainerInner: {
        css: css`
            text-align: right;
            display: flex;
        `,
    },
    payTotal: {
        css: css`
            font-weight: 800;
            margin: 0 8px;
        `,
    },
    paymentTitle: {
        css: css`
            font-size: ${getProp("theme.typography.h6.size")};
            font-weight: 600;
            margin-right: 30px;
            color: ${ADIColors.darkBlack};
        `,
    },
    mtPaymentTitle: {
        css: css`
            font-size: ${getProp("theme.typography.h6.size")};
            font-weight: 600;
            margin-right: 40px;
            color: ${ADIColors.darkBlack};
            display: flex;
            flex-direction: column;
        `,
    },
    paymentBtn: {
        css: css`
            border-radius: 3px;
            padding: 0 80px;
        `,
    },
    mtPaymentBtn: {
        css: css`
            border-radius: 3px;
            padding: 0 35px;
        `,
    },
    warningInfoContainer: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-end;
            margin-top: 30px;
        `,
    },
    warningInfoIcon: {
        css: css`
            svg {
                margin-right: 10px;
                transform: scale(1.5);
            }
        `,
    },
    warningInfoText: {
        css: css`
            width: 33%;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            font-weight: 800;
            line-height: 1.29;
            letter-spacing: normal;
            color: ${getColor("common.backgroundContrast")};
        `,
    },
    mtWarningInfoText: {
        css: css`
            width: 100%;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            font-weight: 800;
            line-height: 1.29;
            letter-spacing: normal;
            color: ${getColor("common.backgroundContrast")};
        `,
    },
    negativeAmountMsg: {
        css: css``,
    },
    selectedInvoiceInfoMsg: {
        css: css``,
    },
    selectedInvoiceMsgContainer: {
        css: css`
            display: flex;
            padding: 6px;
            gap: 5px;
            align-items: flex-start !important;
            justify-content: center;
            span {
                color: ${getColor("common.backgroundContrast")};
                font-size: 12px;
                cursor: initial;
            }

            svg {
                width: 15px;
                cursor: pointer;
            }

            > div:last-child {
                align-self: center;
            }
        `,
    },
    toolTip: {
        css: css`
            position: absolute;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            font-weight: normal !important;
            font-stretch: normal;
            font-style: italic;
            line-height: 1.29;
            letter-spacing: normal;
            text-align: center;
            color: ${ADIColors.darkBlue1};
            width: 153px;
            margin: 0 0 1px;
            padding: 6px 8px 16px;
            box-shadow: 2px 2px 5px 0 rgba(0, 0, 0, 0.25);
            background-color: ${ADIColors.gray6};
            display: flex;
            align-items: center;
            border: none;
            border-radius: 5px;
            z-index: 10;
            bottom: 100%;
            display: none;
            :after {
                content: "";
                position: absolute;
                left: 9%;
                top: calc(100% - 10px);
                margin-left: 5px;
                border: 8px solid ${ADIColors.gray6};
                box-shadow: -3px 3px 3px 0 rgba(0, 0, 0, 0.18);
                border-color: transparent transparent ${ADIColors.gray6} ${ADIColors.gray6};
                transform: rotate(315deg);
            }

            &.center-align {
                :after {
                    left: 40%;
                }
            }
        `,
    },
    select: {
        cssOverrides: {
            formField: css`
                flex-basis: 100%;
                max-width: 100%;
            `,
            formInputWrapper: css`
                width: 70px;
            `,
        },
        labelProps: {
            css: css`
                width: unset;
                font-weight: bold;
                padding: 9px 8px 0 0;
            `,
        },
    },
    selectFilter: {
        cssOverrides: {
            formField: css`
                flex-basis: 100%;
                max-width: 100%;
            `,
            formInputWrapper: css`
                width: 85px;
            `,
        },
        labelProps: {
            css: css`
                width: unset;
                font-weight: bold;
                padding: 9px 8px 0 0;
            `,
        },
    },
    sortBtn: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            border-radius: 8px;
            background: ${getColor("common.background")};
            padding: 0 8px;
            margin: 0 10px;
            svg {
                transform: rotateX(180deg);
            }
            &:hover {
                background: ${getColor("common.background")};
            }
        `,
    },
    mtGridItem: {
        css: css`
            max-width: 100%;
            flex-basis: 100%;
            padding: 0 15px;
            &:first-child {
                justify-content: space-between;
                border-bottom: 1px solid ${getColor("common.border")};
                padding: 15px;
            }
            &:nth-child(2) {
                justify-content: space-between;
                padding: 15px 15px 0px;
            }
            &:nth-last-child(2) {
                padding: 0px 15px 15px;
            }
            &:last-child {
                justify-content: space-between;
                border-top: 1px solid ${getColor("common.border")};
                padding: 15px;
                align-items: center;
            }
        `,
    },
    tabGridItem: {
        css: css`
            max-width: 100%;
            flex-basis: 100%;
            width: 100%;
            padding: 8px 16px;
            border-top: 1px solid ${ADIColors.gray2};
            background: ${getColor("common.background")};
            margin-bottom: 12px !important;
            justify-content: flex-end;
            align-items: center;
        `,
    },
    tabPrint: {
        css: css`
            position: absolute;
            left: 15px;
        `,
    },
    mtDateColumnBody: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            font-weight: bold;
                            color: #002856;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    mtInvoiceViewDetailsBody: {
        css: css`
            svg {
                width: 20px;
                height: 20px;
                stroke-width: 3;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 14px;
                            font-weight: bold;
                            color: #006ae3;
                            letter-spacing: 0.88px;
                            display: flex;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    mtInvoiceLinkBody: { css: css`` },
    mtPoColumnBody: { css: css`` },
    mtDueDateColumnBody: { css: css`` },
    mtDpdColumnBody: { css: css`` },
    mtCurrentBalColumnBody: {
        css: css`
            span {
                font-weight: bold;
            }
        `,
    },
    mtActionColumnBody: {
        css: css`
            max-width: max-content;
            flex-basis: content;
            display: flex;
            justify-content: flex-end;
            color: ${ADIColors.blue5};
            min-width: 47%;
            span {
                font-weight: 800 !important;
                cursor: pointer;
            }
            &.disabled {
                color: ${ADIColors.darkBlack3} !important;
                opacity: 1 !important;
            }
            > div {
                width: 100%;
            }
        `,
    },
    tabActionColumnBody: {
        css: css`
            max-width: max-content;
            flex-basis: content;
            display: flex;
            justify-content: flex-end;
            color: ${ADIColors.blue5};
            min-width: 25%;
            span {
                font-weight: 800 !important;
                cursor: pointer;
            }
            > div {
                width: 100%;
            }
        `,
    },
    mobilecurrentBalColumnBody: {
        css: css`
            max-width: max-content;
            flex-basis: content;
            min-width: 47%;
            display: flex;
            justify-content: flex-end;
            color: ${ADIColors.blue5};
            margin: auto 0 5px;
            span {
                font-weight: 700;
                cursor: pointer;
            }
            > div {
                width: 100%;
            }
        `,
    },
    tabbularCurrentBalColumnBody: {
        css: css`
            font-size: 16px;
            color: ${ADIColors.darkBlack3} !important;
            width: 100px !important;
            height: 38px !important;
            padding: 10px 8px 9px 16px !important;
            border-radius: 6px !important;
            border: solid 1px ${ADIColors.gray3} !important;
            background-color: ${getColor("common.background")};
            margin-right: 14px;
            margin-bottom: 0px !important;
            text-align: right;
            font-weight: normal !important;
        `,
    },
    mtPaymentTotal: {
        css: css`
            padding: 13px 15px;
            background-color: #fff;
            position: fixed;
            bottom: 0px !important;
            left: 0px;
            width: 100%;
            box-shadow: 0 -3px 8px 0 rgba(0, 0, 0, 0.4);
            z-index: 10;
            flex-wrap: wrap;
        `,
    },
    checkboxImg: {
        css: css`
            margin: 3px 3px 0px 0px;
        `,
    },
};

const styles = tableStyles;
const invoice: string = "Invoice";
const paymentAmount: string = "Payment Amount";
const creditMemo: string = "Credit Memo";

type CheckboxStatus = {
    [key: string]: boolean;
};

const getDisplayRange = (pagination: PaginationInfo, invoiceDefaultParam: any): { start: number; end: number } => {
    const { currentPage, pageSize, totalItemCount } = pagination;
    const start = (currentPage - 1) * pageSize + 1;
    let end = Math.min(currentPage * pageSize, totalItemCount);
    if (pageSize !== invoiceDefaultParam.pageSize) {
        const newPageSize = invoiceDefaultParam.pageSize;
        const newCurrentPage = Math.ceil(start / newPageSize);
        end = Math.min(newCurrentPage * newPageSize, totalItemCount);
    }
    return { start, end };
};
const intialErrorState = {
    contactName: "",
    email: "",
    phoneNumber: "",
    projectName: "",
    notes: "",
    filesAndProduct: "",
};
const FILE_EXTENTIONS = "csv";
let isDuplicatePaymentExists = 0;
const ADIInvoiceHistoryTable = ({
    invoiceData,
    multipleLocationInvocies,
    getInvoiceParams,
    updateInvoicePayment,
    loadADIInvoices,
    invoiceExportHandler,
    history,
    isLoading,
    billTo,
    settingsCollection,
    isReturnToInvoice,
    getCustomerCreditStatus,
}: Props) => {
    const [isAllSelected, setIsAllSelected] = React.useState<boolean>(false);
    const [checkboxStatus, setCheckboxStatus] = React.useState<{ [key: string]: boolean }>({});
    const [invoicesToPay, setInvoicesToPay] = React.useState<InvoicePaymentModel[]>([]);
    const [savedInvoicesToPay, setSavedInvoicesToPay] = React.useState<InvoicePaymentModel[]>([]);
    const [finalinvoicesToPay, setFinalInvoicesToPay] = React.useState<InvoicePaymentModel[]>([]);
    const [openDropdowns, setOpenDropdowns] = React.useState<{ [key: string]: boolean }>({});
    const [seletecInvoices, setSeletedInvoices] = React.useState<Invoice[]>([]);
    const [deletedInvoices, setDeletedInvoices] = React.useState<Invoice[]>([]);
    const [sortType, setSortType] = React.useState("");
    const [openFilterType, setOpenFilterType] = React.useState(false);
    const [openDPDFilterType, setOpenDPDFilterType] = React.useState(false);
    const [isMobile, setIsMobile] = React.useState(false);
    const [isTab, setIsTab] = React.useState(false);
    const [mobileAsc, setMobileAsc] = React.useState("DESC");
    const [getInvoicesFromSession, setGetInvoiesFromSession] = React.useState(false);
    const [isErpAvailable, setIsErpAvailable] = React.useState(true);
    const [atleastOneIsSelected, setatleastOneIsSelected] = React.useState(false);
    /// adding for file import --- P21 changes
    const [isImportModalOpen, setIsImportModalOpen] = React.useState(false);
    const [formErrors, setFormErrors] = React.useState(intialErrorState);
    const [files, setFiles] = React.useState<any[]>([]);
    const [dropzone, setDropzone] = React.useState<any>();
    const [fileCollection, setFileCollection] = React.useState<FileCollection[] | []>([]);
    const [invalidMessage, setInvalidMessage] = React.useState<string[]>([]);
    const [totalfilesizeInMB, setTotalfilesizeInMB] = React.useState(0);
    const [fileContent, setFileContent] = React.useState<any[]>([]);
    const [selectedCount, setSelectedCount] = React.useState(0);
    const [showSelectAllCheckBox, setShowSelectAllCheckBox] = React.useState(true);
    const [showAllowInvoicePayment, setAllowWebpayments] = React.useState(true);
    const unSelectedAllInvocie = React.useRef(true);
    const dispatch = useDispatch();

    const keyExistsInArray = (key: any, array: any) => {
        return array.some((item: any) => item.invoiceNumber.trim() == key.trim());
    };
    const isAvailableInCurrentPage = () => {
        let count = 0;
        let result = false;
        let existsInStatus;
        invoiceData?.invoices.forEach(item => {
            const key = item.invoiceNumber.trim();
            existsInStatus = checkboxStatus.hasOwnProperty(key) && checkboxStatus[key] === true;
            if (existsInStatus) {
                count++;
            }
        });
        if (count > 0) {
            result = true;
        }
        return count;
    };
    React.useEffect(() => {
        if (!isLoading) {
            setTimeout(() => {
                const available = !!isAvailableInCurrentPage();
                setIsAllSelected(available);
            });
        }
    }, [isLoading]);

    React.useEffect(() => {
        if (invoiceData) {
            setIsErpAvailable(invoiceData.isErpAvailable);
        }
    }, [invoiceData?.isErpAvailable]);
    React.useEffect(() => {
        if (isReturnToInvoice) {
            const selectedInvoicesString = sessionStorage.getItem("selectedInvoices");

            if (selectedInvoicesString !== null && selectedInvoicesString !== undefined) {
                if (isLoading) return;
                const selectedSessionInvoices: InvoicePaymentModel[] = JSON.parse(selectedInvoicesString);
                setSavedInvoicesToPay(selectedSessionInvoices);
                const selectedCheckBoxes = selectedSessionInvoices.reduce(
                    (acc, invoice) => ({ ...acc, [invoice.InvoiceId.trim()]: true }),
                    {},
                );
                setCheckboxStatus(selectedCheckBoxes);
                setGetInvoiesFromSession(true);
            }
            dispatch({
                type: "Custom/UpdateRetrunToInvocie",
                result: false,
            });
        }
    }, [isReturnToInvoice]);

    React.useEffect(() => {
        const onWindowResize = () => {
            const windowWidth = window.innerWidth;
            setIsMobile(windowWidth < 700);
            setIsTab(windowWidth >= 701 && windowWidth <= 1024);
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    // below commented code US spire Change
    // React.useEffect(() => {
    //     const checkedValues = Object.values(checkboxStatus).filter(item => item);

    //     const selectedInvoicesString = sessionStorage.getItem("selectedInvoices");
    //     let invoicesSelected: any = [];
    //     let existsInArray1 = false;
    //     Object.keys(checkboxStatus).forEach(key => {
    //         existsInArray1 = keyExistsInArray(key, invoiceData?.invoices);

    //         if (existsInArray1) {
    //             invoicesSelected = invoiceData?.invoices.filter((invoice) => {
    //                 const trimmedInvoiceNumber = invoice.invoiceNumber.trim();
    //                 return checkboxStatus[trimmedInvoiceNumber] == true;
    //             });
    //         } else {
    //             if(selectedInvoicesString !== null && selectedInvoicesString !== undefined){
    //                 invoicesSelected = JSON.parse(selectedInvoicesString).filter((invoice: any) => {
    //                     return checkboxStatus[invoice.InvoiceId.trim()] == true;
    //                 });
    //             }
    //         }
    //     });

    //     let selectedInvLengthInCurrentPage = isAvailableInCurrentPage();
    //     let totalInvoiceCount = invoiceData?.invoices?.length && invoiceData?.pagination?.defaultPageSize === invoiceData?.invoices.length ? invoiceData.pagination.defaultPageSize : invoiceData?.invoices?.length || 0;

    //     if (getInvoiceParams.customerSequence !== "ALLInv") {
    //         if(selectedInvLengthInCurrentPage !== 0){
    //             if(selectedInvLengthInCurrentPage == 0){
    //                 setatleastOneIsSelected(false);
    //                 setIsAllSelected(false);
    //             }
    //             else if(selectedInvLengthInCurrentPage > 0 && selectedInvLengthInCurrentPage !== totalInvoiceCount){
    //                 setatleastOneIsSelected(true);
    //                 setIsAllSelected(false);
    //             }
    //             else if (selectedInvLengthInCurrentPage === totalInvoiceCount){
    //                 setatleastOneIsSelected(false);
    //                 setIsAllSelected(true);
    //             }
    //         }
    //         else{
    //             setatleastOneIsSelected(false);
    //             setIsAllSelected(false);
    //         }
    //     }
    //     const invoicesDeleted = invoiceData?.invoices.filter(
    //         invoice => checkboxStatus[invoice.invoiceNumber.trim()] === false,
    //     );
    //     setDeletedInvoices(invoicesDeleted || []);
    //     setSeletedInvoices(invoicesSelected || []);
    //     setSelectedCount(checkedValues ? checkedValues.length : 0);
    // }, [checkboxStatus, invoiceData?.invoices]);

    // below useEffect complete code implementation ---- P21 Change
    React.useEffect(() => {
        const checkedValues = Object.values(checkboxStatus).filter(item => item);
        if (invoiceData?.invoices && invoiceData?.invoices.length <= checkedValues.length) {
            setIsAllSelected(true);
        } else if (
            isDuplicatePaymentExists > 0 &&
            checkedValues.length &&
            invoiceData?.invoices &&
            invoiceData?.invoices.length === checkedValues.length
        ) {
            setIsAllSelected(true);
        } else {
            setIsAllSelected(false);
        }

        const invoicesSelected = invoiceData?.invoices.filter(
            (invoice: any) => checkboxStatus[invoice.invoiceNumber] === true,
        );
        const invoicesDeleted = invoiceData?.invoices.filter(
            (invoice: any) => checkboxStatus[invoice.invoiceNumber] === false,
        );
        setDeletedInvoices(invoicesDeleted || []);
        setSeletedInvoices(invoicesSelected || []);
        setSelectedCount(checkedValues ? checkedValues.length : 0);
        if (isDuplicatePaymentExists === 0) setatleastOneIsSelected(true);
        if (
            isDuplicatePaymentExists > 0 &&
            checkedValues.length &&
            invoiceData?.invoices &&
            invoiceData?.invoices.length >= checkedValues.length
        )
            setatleastOneIsSelected(true);

        if (!invoicesSelected?.length) {
            setatleastOneIsSelected(false);
            setSeletedInvoices([]);
        }
    }, [checkboxStatus, invoiceData?.invoices]);

    React.useEffect(() => {
        if (invoicesToPay.length && unSelectedAllInvocie.current) {
            setFinalInvoicesToPay(
                invoicesToPay.filter(
                    invoice =>
                        Number(invoice.InvoicePaymentAmount) <= Number(invoice.BalanceDue) &&
                        Number(invoice.InvoicePaymentAmount) !== 0,
                ),
            );
        } else {
            setFinalInvoicesToPay([]);
        }

        if (!unSelectedAllInvocie.current) {
            setTimeout(() => {
                unSelectedAllInvocie.current = true;
            }, 100);
        }
    }, [invoicesToPay]);

    React.useEffect(() => {
        if (seletecInvoices.length || deletedInvoices.length) {
            const seletedInvoices: InvoicePaymentModel[] = [];
            deletedInvoices?.forEach(invoice => {
                const saved = invoicesToPay.findIndex(item => item.InvoiceId === invoice.invoiceNumber);
                if (saved > -1) {
                    invoicesToPay.splice(saved, 1);
                }
            });
            seletecInvoices?.forEach(invoice => {
                const saved = invoicesToPay.find(item => item.InvoiceId === invoice.invoiceNumber);

                if (saved) {
                    seletedInvoices.push(saved);
                } else {
                    seletedInvoices.push({
                        ...invoice,
                        InvoiceId: invoice.invoiceNumber,
                        PO: invoice.customerPO,
                        BalanceDue: `${invoice.currentBalance}`,
                        DueDate: invoice.dueDate,
                        InvoiceDate: invoice.invoiceDate,
                        InvoicePaymentAmount: `${invoice.currentBalance}`,
                        InvoiceTotal: `${invoice.invoiceTotal}`,
                        ReasonCode: "0",
                        RefNumber: invoice.terms,
                        TrlrInd: invoice.properties["trlrInd"],
                        Type: invoice.invoiceType,
                        orderNumber: invoice.properties.orderNumber,
                    });
                }
            });

            setInvoicesToPay(prev => {
                const filterNewPaymentInvoices = seletedInvoices?.filter(
                    obj2 => !prev.some(obj1 => obj1.InvoiceId === obj2.InvoiceId),
                );

                const updatedPrevInvoices = prev.filter(
                    invoice =>
                        invoicesToPay.some(item => item.InvoiceId.trim() === invoice.InvoiceId.trim()) &&
                        checkboxStatus[invoice.InvoiceId.trim()] === true,
                );

                const updatedInvoicesToPay = [...updatedPrevInvoices, ...filterNewPaymentInvoices];
                sessionStorage.setItem("selectedInvoices", JSON.stringify(updatedInvoicesToPay));
                setSavedInvoicesToPay(updatedInvoicesToPay);

                return updatedInvoicesToPay;
            });
        } else if (savedInvoicesToPay.length) {
            setInvoicesToPay(savedInvoicesToPay);
            const seletedInvoices: InvoicePaymentModel[] = [];
            deletedInvoices?.forEach(invoice => {
                const saved = savedInvoicesToPay.findIndex(item => item.InvoiceId === invoice.invoiceNumber);
                if (saved > -1) {
                    savedInvoicesToPay.splice(saved, 1);
                }
            });
            seletecInvoices?.forEach(invoice => {
                const saved = savedInvoicesToPay.find(item => item.InvoiceId === invoice.invoiceNumber);

                if (saved) {
                    seletedInvoices.push(saved);
                } else {
                    seletedInvoices.push({
                        InvoiceId: invoice.invoiceNumber,
                        PO: invoice.customerPO,
                        orderNumber: invoice.properties.orderNumber,
                        BalanceDue: `${invoice.currentBalance}`,
                        DueDate: invoice.dueDate,
                        InvoiceDate: invoice.invoiceDate,
                        InvoicePaymentAmount: `${invoice.currentBalance}`,
                        InvoiceTotal: `${invoice.invoiceTotal}`,
                        ReasonCode: "0",
                        RefNumber: invoice.terms,
                        TrlrInd: invoice.properties["trlrInd"],
                        Type: invoice.invoiceType,
                    });
                }
            });

            setInvoicesToPay(prev => {
                const filterNewPaymentInvoices = seletedInvoices?.filter(
                    obj2 => !prev.some(obj1 => obj1.InvoiceId === obj2.InvoiceId),
                );

                const updatedPrevInvoices = prev.filter(invoice =>
                    savedInvoicesToPay.some(item => item.InvoiceId.trim() === invoice.InvoiceId.trim()),
                );

                const updatedInvoicesToPay = [...updatedPrevInvoices, ...filterNewPaymentInvoices];
                sessionStorage.setItem("selectedInvoices", JSON.stringify(updatedInvoicesToPay));
                setSavedInvoicesToPay(updatedInvoicesToPay);

                return updatedInvoicesToPay;
            });
        }
    }, [seletecInvoices, getInvoicesFromSession]);

    const handleSort = (sortType: string) => {
        localStorage.setItem('InvoiceSortPreference', sortType);
        let sort = sortType;

        if (getInvoiceParams?.sort === sort && !getInvoiceParams?.sort.includes("dpd")) {
            sort = `${sort} DESC`;
        } else if (getInvoiceParams?.sort === "dpd,dueDate DESC" && sort.includes("dpd")) {
            sort = "dpd DESC,dueDate";
        } else {
            sort = sortType;
        }

        setSortType(sort);

        loadADIInvoices({
            ...getInvoiceParams,
            sort: sort,
        });
    };

    const showInvoicePaymentPriceInfo = () => {
        const updatedInvoicesToPay = invoicesToPay.map(invoice => {
            return {
                ...invoice,
                InvoicePaymentAmount: Number(invoice.InvoicePaymentAmount).toFixed(2),
            };
        });

        setInvoicesToPay(updatedInvoicesToPay);
        sessionStorage.setItem("selectedInvoices", JSON.stringify(updatedInvoicesToPay));
    };

    const mobileHandleSort = (sortTypeValue: string = sortType) => {
        setSortType(sortTypeValue);

        loadADIInvoices({
            ...getInvoiceParams,
            sort: `${sortTypeValue} ${mobileAsc}`,
        });
    };

    const handleOpenFilter = (event: React.MouseEvent) => {
        event.stopPropagation();
        setOpenFilterType(prev => !prev);
        setOpenDPDFilterType(false);
    };
    const handleOpenDPDFilter = (event: React.MouseEvent) => {
        event.stopPropagation();
        setOpenDPDFilterType(prev => !prev);
        setOpenFilterType(false);
    };

    const changeInvoiceType = (event: React.MouseEvent, value: string) => {
        event.stopPropagation();

        if (getInvoiceParams.invoiceFilterData !== value) {
            loadADIInvoices({
                ...getInvoiceParams,
                invoiceFilterType: "type",
                invoiceFilterData: value,
                properties:
                    value !== "0" ? JSON.stringify({ invoicefiltertype: "type", invoicefilterdata: `${value}` }) : "",
            });
            setOpenFilterType(false);
        }
    };
    const changeInvoiceDPDType = (event: React.MouseEvent, value: string) => {
        event.stopPropagation();

        if (getInvoiceParams.invoiceFilterData !== value) {
            loadADIInvoices({
                ...getInvoiceParams,
                poNumber: "",
                invoiceNumber: "",
                refrencenumber: "",
                invoiceFilterType: "type",
                invoiceFilterData: value !== "0" ? "Invoice" : "0",
                invoiceTypeDesc: "Open",
                invoiceType: "1",
                dpdFilterData: value,
                dpdFilterType: "dpd",
                properties:
                    value !== "0"
                        ? JSON.stringify({
                            dpdfiltertype: "dpd",
                            dpdfilterdata: value,
                        })
                        : "",
            });
            event.stopPropagation();
            setOpenDPDFilterType(prev => !prev);
            setOpenFilterType(false);
        }
    };

    const showInvoicePaymentPrice = (invoiceNumber: string) => {
        const currentInvoice = invoicesToPay.find(invoice => invoice.InvoiceId === invoiceNumber)?.InvoicePaymentAmount;
        return Number(currentInvoice);
    };

    const displayReasonCode = (invoiceNumber: string) => {
        const reasonCode = invoicesToPay.find(invoice => invoice.InvoiceId === invoiceNumber)?.ReasonCode;
        return reasonCode;
    };
    const upateInvoiceAmount = (
        event: React.ChangeEvent<HTMLInputElement>,
        invoiceNumber: string,
        currentBalance: number,
    ) => {
        const updatedInvoicesToPay = invoicesToPay.map(invoice => {
            return {
                ...invoice,
                InvoicePaymentAmount:
                    invoice.InvoiceId === invoiceNumber ? event.target.value : invoice.InvoicePaymentAmount,
                ReasonCode: Number(currentBalance) === Number(event.target.value) ? "0" : invoice.ReasonCode,
            };
        });
        setInvoicesToPay(updatedInvoicesToPay);
        setSavedInvoicesToPay(updatedInvoicesToPay);

        sessionStorage.setItem("selectedInvoices", JSON.stringify(updatedInvoicesToPay));
    };

    const handleReasonCode = (code: string, invoiceNumber: string) => {
        const updatedInvoicesToPay = invoicesToPay.map(invoice => {
            return {
                ...invoice,
                ReasonCode: invoice.InvoiceId === invoiceNumber ? code : invoice.ReasonCode,
            };
        });

        handleDropdownToggle(invoiceNumber);
        setInvoicesToPay(updatedInvoicesToPay);
        sessionStorage.setItem("selectedInvoices", JSON.stringify(updatedInvoicesToPay));
    };

    const checkifSelected = (invoiceNumber: string) => checkboxStatus[invoiceNumber] === true;

    const selectAllChangeHandlerCheckBox: CheckboxProps["onChange"] = (_, value) => {
        // below commented code ---- P21 Change
        if (invoiceData?.invoices) {
            setIsAllSelected(value);
            setCheckboxStatus(prev => {
                // Create the updated status based on the current `value` and conditions
                const updatedStatus = value
                    ? invoiceData?.invoices.reduce((acc: CheckboxStatus, invoice) => {
                        const invoiceNumber = invoice.invoiceNumber.trim();
                        const newValue =
                            getInvoiceParams.customerSequence === "ALLInv"
                                ? invoice.properties.showInvPrintPayLink.toLowerCase() === "true"
                                : true;

                        // Only add the invoice to the state if newValue is true or it was already true in prev
                        if (newValue || prev[invoiceNumber] === true) {
                            acc[invoiceNumber] = newValue;
                        }

                        return acc;
                    }, {})
                    : {};

                // Remove any keys from prev that are false
                Object.keys(prev).forEach(key => {
                    if (prev[key] === false) {
                        delete prev[key];
                    }
                });

                return {
                    ...prev,
                    ...updatedStatus,
                };
            });
        }
        if (!value) {
            setFinalInvoicesToPay([]);
        }

        // took below code from Us Spire
        // if (checkLocCount(invoiceData?.invoices)) {
        //     setShowSelectAllCheckBox(true);
        //     if (invoiceData?.invoices) {
        //         setIsAllSelected(value);
        //         setCheckboxStatus(prev => {
        //             // Create the updated status based on the current `value` and conditions
        //             const updatedStatus = value
        //                 ? invoiceData?.invoices.reduce((acc: CheckboxStatus, invoice) => {
        //                       const invoiceNumber = invoice.invoiceNumber.trim();
        //                       const newValue =
        //                           getInvoiceParams.customerSequence === "ALLInv"
        //                               ? invoice.properties.showInvPrintPayLink.toLowerCase() === "true"
        //                               : true;

        //                       // Only add the invoice to the state if newValue is true or it was already true in prev
        //                       if (newValue || prev[invoiceNumber] === true) {
        //                           acc[invoiceNumber] = newValue;
        //                       }

        //                       return acc;
        //                   }, {})
        //                 : {};

        //             // Remove any keys from prev that are false
        //             Object.keys(prev).forEach(key => {
        //                 if (prev[key] === false) {
        //                     delete prev[key];
        //                 }
        //             });

        //             return {
        //                 ...prev,
        //                 ...updatedStatus,
        //             };
        //         });
        //     }
        //     if (!value) {
        //         setFinalInvoicesToPay([]);
        //     }
        // } else {
        //     setShowSelectAllCheckBox(false);
        // }
    };

    const selectAllChangeHandler = (value: boolean) => {
        //below code commented ----- P21 Change
        // if (!value) {
        //     unSelectedAllInvocie.current = false;
        //     setIsAllSelected(false);
        //     setatleastOneIsSelected(false);
        //     deSeletAllInvoices();
        //     setSavedInvoicesToPay([]);
        //     setFinalInvoicesToPay([]);
        //     setInvoicesToPay([]);
        //     setSeletedInvoices([]);
        // }
        // if (invoiceData?.invoices && value && isDuplicatePaymentExists === 0) {
        //     setIsAllSelected(value);
        //     setCheckboxStatus(
        //         value
        //             ? invoiceData?.invoices.reduce((acc, invoice) => {
        //                 if (invoice.properties?.isDuplicateInvoicePayment === undefined) {
        //                     return { ...acc, [invoice.invoiceNumber]: true };
        //                 } else{
        //                     return {}
        //                 }
        //             }, {})
        //             // invoiceData?.invoices.reduce((acc, invoice) => ({ ...acc, [invoice.invoiceNumber]: (invoice.properties?.isDuplicateInvoicePayment === undefined ? true : false) }), {})
        //             // ? invoiceData?.invoices.reduce((acc, invoice) => ({ ...acc, [invoice.invoiceNumber]: true }), {})
        //             : {},
        //     );
        // } else if(isDuplicatePaymentExists > 0){
        //         setIsAllSelected(false);
        //         deSeletAllInvoices();
        //         setFinalInvoicesToPay([]);
        //     }

        //below code has taken latest from US spire
        if (!value) {
            unSelectedAllInvocie.current = false;
            setIsAllSelected(false);
            setatleastOneIsSelected(false);
            deSeletAllInvoices();
            // setSavedInvoicesToPay([]);
            // setFinalInvoicesToPay([]);
            // setInvoicesToPay([]);
            // setSeletedInvoices([]);
        }
        if (invoiceData?.invoices && value) {
            setIsAllSelected(value);
            setCheckboxStatus(prev => {
                // Create the updated status based on the current `value` and conditions
                const updatedStatus = value
                    ? invoiceData?.invoices.reduce((acc: CheckboxStatus, invoice) => {
                        const invoiceNumber = invoice.invoiceNumber.trim();
                        const newValue =
                            getInvoiceParams.customerSequence === "ALLInv"
                                ? invoice.properties.showInvPrintPayLink.toLowerCase() === "true"
                                : true;

                        // Only add the invoice to the state if newValue is true or it was already true in prev
                        if (newValue || prev[invoiceNumber] === true) {
                            acc[invoiceNumber] = newValue;
                        }

                        return acc;
                    }, {})
                    : {};

                // Remove any keys from prev that are false
                Object.keys(prev).forEach(key => {
                    if (prev[key] === false) {
                        delete prev[key];
                    }
                });

                return {
                    ...prev,
                    ...updatedStatus,
                };
            });
        }
    };

    const dropdownRef = React.useRef<HTMLDivElement>(null);

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            handleDropdownToggle("");
        }
    };

    React.useEffect(() => {
        // adding functionality to disable payment recap button
        const allowPaymentResponse = getCustomerCreditStatus?.getcustomerStatus?.allowWebPayments;
        if (allowPaymentResponse) {
            const allowPaymentFlag = allowPaymentResponse === "Y" ? true : false;

            setAllowWebpayments(allowPaymentFlag);
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [getCustomerCreditStatus?.getcustomerStatus?.allowWebPayments]);

    const formatedDateWithDot = (formattedDate: any) => {
        let dotAddedDate = "";
        if (formattedDate !== "") {
            const parts = formattedDate.split(" ");
            dotAddedDate = `${parts[0]}. ${parts[1]} ${parts[2]}`;
        }
        return dotAddedDate;
    };

    const deSeletAllInvoices = () => {
        if (invoiceData?.invoices) {
            setCheckboxStatus(prev => {
                const updatedCheckboxStatus = { ...prev };

                invoiceData.invoices.forEach(invoice => {
                    const invoiceNumber = invoice.invoiceNumber.trim();
                    if (invoiceNumber in updatedCheckboxStatus) {
                        delete updatedCheckboxStatus[invoiceNumber];
                    }
                });

                return updatedCheckboxStatus;
            });
        }
        handleDropdownToggle("");
    };

    const handleDropdownToggle = (rowId: string) => {
        setOpenDropdowns(prevOpenDropdowns => {
            const isOpen = prevOpenDropdowns[rowId];
            const updatedOpenDropdowns = { [rowId]: !isOpen };

            // Close other open dropdowns
            if (!isOpen) {
                for (const id in prevOpenDropdowns) {
                    if (id !== rowId) {
                        updatedOpenDropdowns[id] = false;
                    }
                }
            }

            return updatedOpenDropdowns;
        });
    };
    const onCheckboxChange = (itemId: string) => {
        setCheckboxStatus(prev => {
            // Create a new status object by copying the previous state
            const updatedStatus: CheckboxStatus = { ...prev };

            // Trim the itemId
            const trimmedItemId = itemId.trim();

            // Check if the previous value was true
            if (prev[trimmedItemId] === true) {
                // If the previous value was true, delete it from the state
                delete updatedStatus[trimmedItemId];
            } else {
                // Otherwise, toggle the value (false to true or undefined to true)
                updatedStatus[trimmedItemId] = true;
            }

            return updatedStatus;
        });
    };

    const onClickPrint = (invoiceNumber: string, InvType: string) => {
        if (!invoiceData) {
            return;
        }

        const printUrl = `MyAccount/Invoices/Details?invoiceNumber=`;
        const fullUrl = printUrl + invoiceNumber.trim() + "&InvType=" + InvType + "&print=1";

        const newTab = window.open(fullUrl, "_blank");

        if (newTab && fullUrl.includes("print=1")) {
            newTab.focus();
        }
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10).toString();
        loadADIInvoices({
            ...getInvoiceParams,
            page: "1",
            pageSize: newPageSize,
        });
    };

    const handleMobileSort = (value: string) => {
        let sort;

        if (value === "") {
            sort = "DESC";
            setMobileAsc("DESC");
        } else {
            sort = "";
            setMobileAsc("");
        }

        loadADIInvoices({
            ...getInvoiceParams,
            sort: `${sortType} ${sort}`,
        });
    };

    const handleExport = () => {
        if (invoiceData) invoiceExportHandler({ ...getInvoiceParams, page: "1" });
    };
    const handleImport = () => {
        setIsImportModalOpen(true);
    };
    const dataTableHeaderArray = [
        { name: "Date", value: "invoiceDate" },
        { name: "Type", value: "invoiceType" },
        { name: "Invoice", value: "invoiceNumber" },
        { name: "Order #", value: "orderNumber" },
        { name: "PO#", value: "customerPO" },
        { name: "Due Date", value: "dueDate" },
        { name: "Balance", value: "CurrentBalance" },
    ];
    if (getInvoiceParams.customerSequence === "ALLInv") {
        dataTableHeaderArray.push({ name: "Loc.", value: "customerNumber" });
    }

    const formatYearData = (dateString: string) => {
        if (!dateString) return;

        const date = new Date(dateString);

        function getAbbreviatedMonthName(month: number) {
            month = Math.max(0, Math.min(11, month));
            return new Date(0, month).toLocaleDateString("en-US", { month: "short" });
        }

        const month = getAbbreviatedMonthName(date.getMonth());
        const day = date.getDate();
        const year = date.getFullYear();
        const formattedDate = month + " " + day + ", " + year;
        return formattedDate;
    };

    const allInvoicesTotal = (allInvoices: InvoicePaymentModel[]) => {
        const total = allInvoices.reduce((acc, item) => acc + Number(item.InvoicePaymentAmount), 0);
        return Number(total.toFixed(2));
    };

    const updatePaymentHandler = () => {
        updateInvoicePayment({
            invoicehistorycollection: finalinvoicesToPay.filter(
                fv => fv.properties.isDuplicateInvoicePayment !== "true",
            ),
            onComplete(result) {
                if (result.apiResult) {
                    history.push(`${siteMessage("invoice_recappage_url")}`);
                }
            },
        });
    };

    const checkCustomerSequenceMatchesBillTo = (invoiceCustomerNumber: string, invoiceType: string) => {
        let matches = false;
        const invoiceCustomerParts = invoiceCustomerNumber.split("-");
        const invoiceCustomerSequence = invoiceCustomerParts[1] !== undefined ? invoiceCustomerParts[1] : null;

        if (billTo) {
            const billToCustomerParts = billTo.customerNumber.split("-");
            const billToCustomerSequence =
                billToCustomerParts && billToCustomerParts[1] !== undefined ? billToCustomerParts[1] : null;

            if (invoiceType === "Invoice" || invoiceType === "Credit Memo" || invoiceType === "Account Credit") {
                matches = invoiceCustomerSequence === billToCustomerSequence;
            }
        }

        return matches;
    };

    const checkLocCount = (invoiceData: any, type = "") => {
        let hasCount = false;
        let invCount = 0;
        invoiceData.map((invoice: any) => {
            if (checkCustomerSequenceMatchesBillTo(invoice.customerNumber, invoice.invoiceType)) {
                invCount++;
            }
        });
        setIsAllSelected(false);
        if (invCount > 0) {
            hasCount = true;
            if (type == "") {
                setIsAllSelected(true);
            }
        }
        return hasCount;
    };

    React.useEffect(() => {
        if (invoiceData?.invoices && getInvoiceParams.customerSequence === "ALLInv") {
            setShowSelectAllCheckBox(false);
            //to show/hide Select all checkbox based on pagination for View all invoices tab
            if (checkLocCount(invoiceData?.invoices)) {
                setShowSelectAllCheckBox(true);
            }
        }
        if (invoiceData?.invoices && getInvoiceParams.customerSequence == "-1") {
            const invoiceContainer = document.querySelector(".DESC");
            const invoiceContainer2 = document.querySelector(".non-selected");
            const invoiceContainer3 = document.querySelector(".ASC");
            if (invoiceContainer) {
                invoiceContainer.classList.add("viewalllocationinvoice");
            }
            if (invoiceContainer3) {
                invoiceContainer3.classList.add("viewalllocationinvoiceasc");
            }

            if (invoiceContainer2) {
                invoiceContainer2.parentElement?.classList.add("viewalllocationinvoiceinside");
            }
        }
    }, [invoiceData?.invoices]);

    if (!invoiceData || multipleLocationInvocies) {
        return null;
    }
    const { start, end } = getDisplayRange(invoiceData.pagination, getInvoiceParams);
    const invoiceMaximumImportLimit = settingsCollection.adiInvoiceHistorySettingSettings.invoiceMaximumImportLimit;
    const totalItemCountValue = invoiceData.pagination.totalItemCount;
    const formattedTotalItemCountValue = totalItemCountValue.toLocaleString();
    const reasonCodes: ReasonCodes[] = invoiceData?.properties?.reasonCodes
        ? JSON.parse(invoiceData?.properties?.reasonCodes)
        : [];

    if (!invoiceData.invoices.length) {
        return (
            <StyledWrapper {...styles.noInvoicesContainer}>
                <StyledWrapper {...styles.noInvoicesContent}>
                    <Typography as={"span"}>
                        {translate(`We couldn't find any invoices matching your search criteria.`)}
                    </Typography>
                    <Typography as={"span"}>{translate(`Try searching another PO or invoice number.`)}</Typography>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const showPageSizes = invoiceData?.pagination?.totalItemCount > invoiceData?.pagination?.defaultPageSize;
    const DPD1to30 = invoiceData?.properties?.disableBalance1_30;
    const DPD31to60 = invoiceData?.properties?.disableBalance31_60;
    const DPD61to90 = invoiceData?.properties?.disableBalance61_90;
    const DPDover90 = invoiceData?.properties?.disableBalanceOver90;
    const disableTypeCM = invoiceData.properties.disableTypeCM;
    // below method ----P21 Change
    const getIndex = (colName: string, csvContent: any) => {
        let index: number = -1;
        let contentRow: any = csvContent[0];
        for (let i = 0; i < contentRow.length; i++) {
            if (colName.toString().toLowerCase() == contentRow[i].toString().toLowerCase()) {
                index = i;
            }
            if (index == -1) {
                if (colName.toString().toLowerCase() === contentRow[i].toString().toLowerCase()) {
                    index = i;
                }
            }
            if (index != -1) {
                return index;
            }
        }
        return index;
    };
    const ConvertToDecimal = (num: any) => {
        num = num.toString(); //If it's not already a String
        num = num.slice(0, num.indexOf(".") + 3); //With 3 exposing the hundredths place
        return num;
    };
    const checkP1Validation = (fileExtention: string, csvContent: any, fileName: string) => {
        if (fileExtention !== "csv") {
            setInvalidMessage(["Invalid file. Please try uploading your file again in CSV format."]);
            return false;
        }
        // if (fileContent && fileContent.length > 0) {
        //     csvContent = fileContent;
        // }
        if (csvContent != "" && csvContent?.length > 0) {
            if (getIndex(invoice, csvContent) == -1 || getIndex(paymentAmount, csvContent) == -1) {
                setInvalidMessage([
                    "Invalid file. Please try uploading your file again with the invoice and payment amount columns included.",
                ]);
                return false;
            }
            if (getIndex("Invoice", csvContent) != -1 && getIndex(paymentAmount, csvContent) != -1) {
                let index: number;
                let bBlank: boolean = true;
                for (index = 1; index < csvContent.length; index++) {
                    if (csvContent[index] != "") {
                        bBlank = false;
                    }
                }

                if (bBlank) {
                    setInvalidMessage([
                        "This file is empty. Please try uploading your file again with the invoice and payment amount data included",
                    ]);
                    return false;
                }
            }

            if (csvContent.length - 1 >= invoiceMaximumImportLimit + 1) {
                setInvalidMessage(["You have reached the maximum limit for invoices"]);
                return false;
            }
        }
        // if (fileName != "") {
        //     setInvalidMessage("Only one file can be uploaded at a time");
        //     return false;
        // }
        if (csvContent.length == 0 || csvContent.length == 1) {
            setInvalidMessage([
                "This file is empty. Please try uploading your file again with the invoice and payment amount data included",
            ]);
            return false;
        }
        return true;
    };

    // P2 Validations for Import Invoices from CSV
    const checkP2Validation = (csvContent: any) => {
        // if (fileContent && fileContent?.length > 0) {
        //     csvContent = fileContent;
        // }
        let indexInvoice = getIndex(invoice, csvContent);
        let indexPaymentAmount = getIndex(paymentAmount, csvContent);
        let index = 1;
        type importFileRow = {
            SNo: number;
            Invoice: string;
            PaymentAmount: string;
        };
        let importFileCollection = [] as importFileRow[];
        csvContent.forEach(function (item: any) {
            if (index > 1 && item[indexInvoice] != undefined && item[indexPaymentAmount] != undefined) {
                importFileCollection.push({
                    SNo: index,
                    Invoice: item[indexInvoice].trim(),
                    PaymentAmount: item[indexPaymentAmount].replace(/[\r]/g, "").trim(),
                });
            }
            index++;
        });
        let invoiceNumbers: string[] = [];
        importFileCollection.forEach(function (item) {
            invoiceNumbers.push(item.Invoice);
        });
        let findDuplicatesInvoices = (arr: any) => arr.filter((item: any, index: number) => arr.indexOf(item) != index);
        let duplicateInvoices = new Set(findDuplicatesInvoices(invoiceNumbers));
        let errorMessages: string[] = [];
        let errorMsgCount: number = 0;
        for (let item of importFileCollection) {
            let importAmount = Number(
                item.PaymentAmount.replace(/\$/g, "").replace(/\(/g, "-").replace(/\)/g, "").replace(/\,/g, ""),
            );
            //Invoice Not Found - 1st
            if (typeof item.Invoice == "undefined" || !item.Invoice.trim()) {
                errorMessages.push(("0" + item.SNo).slice(-2) + ": " + translate("Invoice not found"));
                errorMsgCount++;
                if (errorMsgCount >= 10) {
                    break;
                }
            }
            //PaymentAmount Not Found - 2nd
            if (typeof item.PaymentAmount == "undefined" || !item.PaymentAmount) {
                errorMessages.push(("0" + item.SNo).slice(-2) + ": " + translate("Payment amount not found"));
                errorMsgCount++;
                if (errorMsgCount >= 10) {
                    break;
                }
            }
            //Amount Valid Examples/Invalid payment amount - 6th
            let paymentAmountError: boolean = false;
            let regex = /^\(?\-?\$?[0-9]+(,\d+)*(\.[0-9]{1,9})?\)?$/;
            if (!(typeof item.PaymentAmount == "undefined" || !item.PaymentAmount)) {
                if (!regex.test(item.PaymentAmount)) {
                    errorMessages.push(("0" + item.SNo).slice(-2) + ": " + translate("Invalid payment amount"));
                    errorMsgCount++;
                    paymentAmountError = true;
                    if (errorMsgCount >= 10) {
                        break;
                    }
                }
                if (
                    !(
                        (item.PaymentAmount.includes("(") && item.PaymentAmount.includes(")")) ||
                        (!item.PaymentAmount.includes("(") && !item.PaymentAmount.includes(")"))
                    )
                ) {
                    errorMessages.push(("0" + item.SNo).slice(-2) + ": " + translate("Invalid payment amount"));
                    errorMsgCount++;
                    paymentAmountError = true;
                    if (errorMsgCount >= 10) {
                        break;
                    }
                }
            }
            let invoiceExists: boolean = false;
            if (invoiceData.invoices != null && invoiceData.invoices.length > 0) {
                for (let invoiceRow of invoiceData.invoices) {
                    if (invoiceRow.invoiceNumber.trim() === item.Invoice) {
                        invoiceExists = true;
                        if (!paymentAmountError) {
                            //Paid Invocie and Payment Pending
                            if (invoiceRow?.properties?.isDuplicateInvoicePayment == "true") {
                                errorMessages.push(("0" + item.SNo).slice(-2) + ": " + translate("Payment Pending"));
                                errorMsgCount++;
                                if (errorMsgCount >= 10) {
                                    break;
                                }
                            }
                            //Payment amount greater than invoice amount - 4th
                            if (invoiceRow.invoiceType == invoice && importAmount > invoiceRow.currentBalance) {
                                errorMessages.push(
                                    ("0" + item.SNo).slice(-2) +
                                    ": " +
                                    translate("Payment amount greater than invoice amount"),
                                );
                                errorMsgCount++;
                                if (errorMsgCount >= 10) {
                                    break;
                                }
                            }
                            //Payment amount equal or less than zero - 5th
                            if (!(typeof item.PaymentAmount == "undefined" || !item.PaymentAmount)) {
                                if (
                                    invoiceRow.invoiceType == invoice &&
                                    ConvertToDecimal(importAmount.toFixed(2)) <= 0
                                ) {
                                    errorMessages.push(
                                        ("0" + item.SNo).slice(-2) + ": " + translate("Invalid payment amount"),
                                    );
                                    errorMsgCount++;
                                    if (errorMsgCount >= 10) {
                                        break;
                                    }
                                }
                                //Invalid PAyment Amount for credit Memo - 7th
                                if (
                                    invoiceRow.invoiceType == invoice &&
                                    (importAmount.toString() != invoiceRow.currentBalance.toString() ||
                                        item.PaymentAmount.indexOf("-") != -1)
                                ) {
                                    errorMessages.push(
                                        ("0" + item.SNo).slice(-2) + ": " + translate("Invalid payment amount"),
                                    );
                                    errorMsgCount++;
                                    if (errorMsgCount >= 10) {
                                        break;
                                    }
                                }
                            }
                        }
                        //Pay/Apply Button displayed/Enabled for Invoice : check showPayButton - 8th : ToDo
                        if (
                            !(
                                (invoiceRow.invoiceType == invoice || invoiceRow.invoiceType == creditMemo) &&
                                invoiceRow.properties["showPayButton"] == "true" &&
                                invoiceData.properties["showPaymentButton"] == "true"
                            )
                        ) {
                            errorMessages.push(
                                ("0" + item.SNo).slice(-2) + ": " + translate("Payment amount not found"),
                            );
                            errorMsgCount++;
                            if (errorMsgCount >= 10) {
                                break;
                            }
                        }
                    }
                    if (invoiceExists) {
                        break;
                    }
                }
                if (errorMsgCount >= 10) {
                    break;
                }
            }
            if (errorMsgCount >= 10) {
                break;
            }
            //Invoice Not Found - 3rd
            if (!(typeof item.Invoice == "undefined" || !item.Invoice.trim())) {
                if (!invoiceExists) {
                    errorMessages.push(("0" + item.SNo).slice(-2) + ": " + translate("Invoice not found"));
                    errorMsgCount++;
                    if (errorMsgCount >= 10) {
                        break;
                    }
                }
                //Duplicate Invoice Number - 9th
                if (duplicateInvoices.has(item.Invoice)) {
                    errorMessages.push(("0" + item.SNo).slice(-2) + ": " + translate("Duplicate invoice number"));
                    errorMsgCount++;
                    if (errorMsgCount >= 10) {
                        break;
                    }
                }
            }
        }
        if (errorMsgCount > 0) {
            const uniqueErrorMessages = Array.from(new Set(errorMessages));
            setInvalidMessage(uniqueErrorMessages);
            return false;
        } else {
            return true;
        }
    };

    // P3 Validations for Import Invoices from CSV
    const checkP3Validation = (csvContent: any) => {
        // if (fileContent && fileContent.length > 0) {
        //     csvContent = fileContent;
        // }
        const validationMessage = "";
        let payTotal: number = 0;
        let invTotal: number = 0;
        let cmTotal: number = 0;
        let indexInvoice = getIndex(invoice, csvContent);
        let indexPaymentAmount = getIndex(paymentAmount, csvContent);
        let index = 0;
        if (invoiceData != null && invoiceData.invoices.length > 0) {
            for (let row of csvContent) {
                if (index > 0 && row[indexInvoice] != undefined && row[indexPaymentAmount] != undefined) {
                    let invType = invoiceData.invoices.find(
                        (inv: any) => inv.invoiceNumber.trim() == row[indexInvoice].trim(),
                    )?.invoiceType;
                    if (invType == invoice) {
                        invTotal =
                            invTotal +
                            ConvertToDecimal(parseFloat(row[indexPaymentAmount].replace(/[^0-9\.-]+/g, "")).toFixed(2));
                    } else if (invType == creditMemo) {
                        let cmValue = ConvertToDecimal(
                            parseFloat(row[indexPaymentAmount].replace(/[^0-9\.-]+/g, "")).toFixed(2),
                        );
                        cmTotal = cmTotal + parseFloat(cmValue);
                    }
                }
                index++;
            }
        }
        payTotal = invTotal - cmTotal;
        if (payTotal <= 0) {
            setInvalidMessage(["You must apply a credit amount that is lesser than the invoice total."]);
            return false;
        }
        return true;
    };
    const onAddFile = (event: React.ChangeEvent<any>) => {
        setFiles([]);
        event.preventDefault();
        if (event.target.files.length > 1) {
            setInvalidMessage([translate("Only one file can be uploaded at a time.")]);
        } else if (event.target.files[0] !== undefined && event.target.files[0].name !== "") {
            fileUpload(event.target.files[0]);
        }
        event.target.files = null;
    };

    const handleDrop = async (event: any) => {
        event.preventDefault();
        if (event.dataTransfer.files.length > 1) {
            setInvalidMessage([translate("Only one file can be uploaded at a time.")]);
        } else if (event.dataTransfer.files[0] !== undefined && event.dataTransfer.files[0].name !== "") {
            fileUpload(event.dataTransfer.files[0]);
        }
        event.target.value = null;
    };

    const fileUpload = async (file: File) => {
        const validFiles: File[] = [];
        let fileExtention = file.name.split(".")[1];
        const fileName = file.name;
        const textData = await readCSVFile(file);
        const csvData = await CSVToArray(textData);
        setFileContent(csvData);
        if (checkP1Validation(fileExtention, csvData, fileName)) {
            // Priority 1 Validations are done
            if (checkP2Validation(csvData)) {
                // Priority 2 Validations are done
                if (checkP3Validation(csvData)) {
                    // Priority 3 Validations are done
                    if (validateFile(file)) {
                        validFiles.push(file);
                        uploadFile(file);
                        setInvalidMessage([]);
                        const csvInvoices = invoiceData?.invoices?.filter(invoice => {
                            return csvData.some((data: any) => {
                                return (
                                    data[0] !== "" &&
                                    data[0]?.toLowerCase() !== "invoice" &&
                                    invoice.invoiceNumber === data[0]
                                );
                            });
                        });
                        if (csvInvoices && csvInvoices.length > 0) {
                            setSeletedInvoices(csvInvoices);
                            const seletedInvoices: InvoicePaymentModel[] = [];
                            csvInvoices?.forEach(invoice => {
                                const saved = savedInvoicesToPay.find(item => item.InvoiceId === invoice.invoiceNumber);
                                if (saved) {
                                    seletedInvoices.push(saved);
                                } else {
                                    seletedInvoices.push({
                                        InvoiceId: invoice.invoiceNumber,
                                        PO: invoice.customerPO,
                                        orderNumber: invoice.properties.orderNumber,
                                        BalanceDue: `${invoice.currentBalance}`,
                                        DueDate: invoice.dueDate,
                                        InvoiceDate: invoice.invoiceDate,
                                        InvoicePaymentAmount: `${invoice.currentBalance}`,
                                        InvoiceTotal: `${invoice.invoiceTotal}`,
                                        ReasonCode: "0",
                                        RefNumber: invoice.terms,
                                        TrlrInd: invoice.properties["trlrInd"],
                                        Type: invoice.invoiceType,
                                    });
                                }
                            });

                            setInvoicesToPay(prev => {
                                const filterNewPaymentInvoices = seletedInvoices?.filter(
                                    obj2 => !prev.some(obj1 => obj1.InvoiceId === obj2.InvoiceId),
                                );

                                const updatedPrevInvoices = prev.filter(invoice =>
                                    savedInvoicesToPay.some(item => item.InvoiceId === invoice.InvoiceId),
                                );

                                const updatedInvoicesToPay = [...updatedPrevInvoices, ...filterNewPaymentInvoices];
                                sessionStorage.setItem("selectedInvoices", JSON.stringify(updatedInvoicesToPay));
                                setSavedInvoicesToPay(updatedInvoicesToPay);

                                return updatedInvoicesToPay;
                            });
                        }
                    }
                }
            }
        }
        setFiles(prev => [...prev, ...validFiles]);
    };
    const readCSVFile = async (file: File): Promise<any> => {
        return new Promise<any>((resolve, reject) => {
            if (!file) {
                resolve("");
            }
            const reader = new FileReader();
            reader.onloadend = e => {
                const text = reader !== null && reader?.result?.toString();
                if (text !== null) resolve(text);
            };
            reader.readAsText(file);
        });
    };

    const CSVToArray = (strData: any, strDelimiter?: string) => {
        // Check to see if the delimiter is defined. If not,
        // then default to comma.
        strDelimiter = strDelimiter || ",";
        // Create a regular expression to parse the CSV values.
        let objPattern = new RegExp(
            // Delimiters.
            "(\\" +
            strDelimiter +
            "|\\r?\\n|\\r|^)" +
            // Quoted fields.
            '(?:"([^"]*(?:""[^"]*)*)"|' +
            // Standard fields.
            '([^"\\' +
            strDelimiter +
            "\\r\\n]*))",
            "gi",
        );
        // Create an array to hold our data. Give the array
        // a default empty first row.
        let arrData: any = [[]];
        // Create an array to hold our individual pattern
        // matching groups.
        let arrMatches = null;
        // Keep looping over the regular expression matches
        // until we can no longer find a match.
        while ((arrMatches = objPattern.exec(strData))) {
            // Get the delimiter that was found.
            let strMatchedDelimiter = arrMatches[1];
            // Check to see if the given delimiter has a length
            // (is not the start of string) and if it matches
            // field delimiter. If id does not, then we know
            // that this delimiter is a row delimiter.
            if (strMatchedDelimiter.length && strMatchedDelimiter !== strDelimiter) {
                // Since we have reached a new row of data,
                // add an empty row to our data array.
                arrData.push([]);
            }
            let strMatchedValue;
            // Now that we have our delimiter out of the way,
            // let's check to see which kind of value we
            // captured (quoted or unquoted).
            if (arrMatches[2]) {
                // We found a quoted value. When we capture
                // this value, unescape any double quotes.
                strMatchedValue = arrMatches[2].replace(new RegExp('""', "g"), '"');
            } else {
                // We found a non-quoted value.
                strMatchedValue = arrMatches[3];
            }
            // Now that we have our value string, let's add
            // it to the data array.
            arrData[arrData.length - 1].push(strMatchedValue);
        }
        // Return the parsed data.
        return arrData;
    };

    const handleDragOver = (event: any) => {
        event.preventDefault();
        // dropzone.style.border = `1px dashed ${ADIColors.gray10}`;
        // dropzone.style.backgroundColor = "gray";
    };
    const handleDragLeave = (event: any) => {
        // dropzone.style.border = "none";
        // dropzone.style.backgroundColor = "transparent";

        event.preventDefault();
    };

    const handleDragStart = (event: any) => {
        event.dataTransfer.setData("text/plain", event.target.id);
    };

    const onRemoveFile = (name: string) => {
        const removedFile = files.find(item => item.name === name);
        if (removedFile) {
            if (totalfilesizeInMB) {
                let bytes = removedFile.size;
                let sizeInMB = (bytes / (1024 * 1024)).toFixed(2);
                let totalsize = (Number(totalfilesizeInMB) - Number(sizeInMB)).toFixed(2);
                setTotalfilesizeInMB(!isNaN(Number(totalsize)) ? Number(totalsize) : 0);
            }
            const newFiles = files.filter(item => item.name !== name);
            const fileCollectionData = fileCollection.filter(item => item.fileName !== name);
            setFiles([...newFiles]);
            setFileCollection([...fileCollectionData]);
        }
    };

    const validateFile = (file: File) => {
        const fileextensions = FILE_EXTENTIONS.split(",");
        if (file.name != "" && !files.some(x => x.name === file.name)) {
            const splittedfilename: string[] = file.name.split(".");
            const currentfileextension =
                splittedfilename.length > 0 ? splittedfilename[splittedfilename.length - 1].toLowerCase() : "";
            const invalidfilextension =
                currentfileextension === fileextensions[fileextensions.indexOf(currentfileextension)] ? false : true;
            if (invalidfilextension) {
                setInvalidMessage([translate("Invalid file. Please try uploading your file again in CSV format.")]);
                return false;
            }

            if (file.size !== 0 && !invalidfilextension) {
                let bytes = file.size;
                let sizeInMB = (bytes / (1024 * 1024)).toFixed(2);
                let totalsize = (Number(totalfilesizeInMB) + Number(sizeInMB)).toFixed(2);
                //ADINA-49083
                const isfilesizeexceed = Number(totalsize) > 20;
                if (!isfilesizeexceed) {
                    setTotalfilesizeInMB(!isNaN(Number(totalsize)) ? Number(totalsize) : 0);
                    return true;
                } else {
                    setInvalidMessage([
                        translate("Please upload a smaller file, or submit another quote for additional files."),
                    ]);
                    return false;
                }
            }

            return true;
        } else {
            return false;
        }
    };

    const uploadFile = (value: any) => {
        let rdr = new FileReader();
        rdr.onloadend = () => {
            let base64data = rdr.result;
            if (base64data) {
                let filedata = base64data.toString().substr(base64data.toString().indexOf(",") + 1);
                setFileCollection(prev => [...prev, { fileName: value.name, fileContentBase64: filedata }]);
            }
        };
        rdr.readAsDataURL(value);
    };
    const getDragZone = (element: any) => {
        setDropzone(element);
    };

    const showPrintIcon = (invoice: any) => {
        let result = false;
        if (
            getInvoiceParams.customerSequence === "ALLInv" &&
            invoice.properties.showInvPrintPayLink.toLowerCase() === "true"
        ) {
            result = true;
        } else if (checkCustomerSequenceMatchesBillTo(invoice.customerNumber, invoice.invoiceType)) {
            result = true;
        }
        return result;
    };

    const showPayButton = (invoice: any) => {
        let result = false;

        if (
            (getInvoiceParams.customerSequence !== "ALLInv" &&
                checkCustomerSequenceMatchesBillTo(invoice.customerNumber, "Invoice")) ||
            (getInvoiceParams.customerSequence === "ALLInv" &&
                invoice.properties.showInvPrintPayLink.toLowerCase() === "true")
        ) {
            result = true;
        }

        return result;
    };

    const showApplyButton = (invoice: any) => {
        let result = false;

        if (
            (getInvoiceParams.customerSequence !== "ALLInv" &&
                checkCustomerSequenceMatchesBillTo(invoice.customerNumber, "Credit Memo")) ||
            (getInvoiceParams.customerSequence === "ALLInv" &&
                invoice.properties.showInvPrintPayLink.toLowerCase() === "true")
        ) {
            result = true;
        }

        return result;
    };

    return (
        <>
            {isMobile ? (
                <>
                    <GridContainer {...styles.exportContainer}>
                        <GridItem {...styles.mtPageresultsGridItem}>
                            <Typography {...styles.mtPageresults}>
                                {translate(`Showing ${start} - ${end}  of ${invoiceData.pagination.totalItemCount}`)}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.mtSelectGridItem}>
                            <Select
                                className="pageSize"
                                {...styles.selectFilter}
                                label={translate("Sort by")}
                                value={sortType}
                                labelPosition="left"
                                onChange={e => mobileHandleSort(e.target.value)}
                                data-test-selector="productListSortSelect"
                            >
                                {dataTableHeaderArray.map(options => (
                                    <option key={options.value} value={options.value}>
                                        {options.name}
                                    </option>
                                ))}
                            </Select>
                            <Button shape="rounded" {...styles.sortBtn} onClick={() => handleMobileSort(mobileAsc)}>
                                <Icon src={SortAmountUp} color="primary" {...styles.Icons} />
                            </Button>
                            {showPageSizes ? (
                                <Select
                                    className="pageSize"
                                    {...styles.select}
                                    label={translate("Per Page")}
                                    value={invoiceData.pagination.pageSize}
                                    labelPosition="left"
                                    onChange={changeResultsPerPage}
                                    data-test-selector="productListSortSelect"
                                >
                                    {invoiceData.pagination.pageSizeOptions
                                        .filter(option => option <= 36)
                                        .map(options => (
                                            <option key={options} value={options}>
                                                {options}
                                            </option>
                                        ))}
                                </Select>
                            ) : null}
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.GridItemInvoices}>
                            {invoiceData.invoices.map((invoice, index: number) => {
                                const customerSequenceMatchesBillTo = checkCustomerSequenceMatchesBillTo(
                                    invoice.customerNumber,
                                    invoice.invoiceType,
                                );
                                const formattedDate = invoice.invoiceDate
                                    ? new Date(invoice.invoiceDate).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                    })
                                    : "";

                                const dotAddedDate = formatedDateWithDot(formattedDate);
                                const formattedDueDate = invoice.invoiceDate
                                    ? new Date(invoice.invoiceDate).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                    })
                                    : "";

                                return (
                                    <GridContainer {...styles.mtSingleInvoiceGridItem} as="li" key={`Item${index}`}>
                                        <GridItem {...styles.mtGridItem}>
                                            <StyledWrapper {...styles.mtDateColumnBody}>{dotAddedDate}</StyledWrapper>
                                            <StyledWrapper {...styles.mtInvoiceViewDetailsBody}>
                                                {/* //adding one more param ---P21 Chnage */}
                                                <InvoiceDetailPageTypeLink
                                                    title={translate("View Details")}
                                                    invoiceNumber={invoice.invoiceNumber}
                                                    invoiceType={invoice.properties.invType}
                                                />
                                                <ChevronRight />
                                            </StyledWrapper>
                                        </GridItem>
                                        <GridItem {...styles.mtGridItem}>
                                            <StyledWrapper {...styles.mtInvoiceLinkBody}>
                                                {translate("Invoice #:")} {invoice.invoiceNumber}
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.mtCurrentBalColumnBody}>
                                                <span>{translate("Balance:")}</span>
                                                {invoice.currentBalanceDisplay}
                                            </StyledWrapper>
                                        </GridItem>
                                        {getInvoiceParams.customerSequence === "ALLInv" && (
                                            <GridItem {...styles.mtGridItem}>
                                                <StyledWrapper {...styles.mtInvoiceLinkBody}>
                                                    {translate("Loc. :")} {invoice.customerNumber.split("-")[1]}
                                                </StyledWrapper>
                                            </GridItem>
                                        )}
                                        <GridItem {...styles.mtGridItem}>
                                            <StyledWrapper {...styles.mtInvoiceLinkBody}>
                                                {translate("Type :")} {invoice.invoiceType}
                                            </StyledWrapper>
                                        </GridItem>
                                        <GridItem {...styles.mtGridItem}>
                                            <StyledWrapper {...styles.mtDueDateColumnBody}>
                                                {translate("Due Date :")}
                                                {(invoice.currentBalance > 0 || getInvoiceParams.invoiceType === "0") &&
                                                    invoice.dueDate !== "-1"
                                                    ? formatYearData(invoice.dueDate)
                                                    : null}
                                            </StyledWrapper>
                                        </GridItem>
                                        <GridItem {...styles.mtGridItem}>
                                            <StyledWrapper {...styles.mtPoColumnBody}>
                                                {translate("PO #:")}
                                                {invoice.customerPO}
                                            </StyledWrapper>
                                        </GridItem>

                                        <GridItem {...styles.mtGridItem}>
                                            <StyledWrapper>
                                                {showPrintIcon(invoice) && (
                                                    <Link
                                                        {...styles.mtPrintButton}
                                                        onClick={() =>
                                                            onClickPrint(
                                                                invoice.invoiceNumber,
                                                                invoice.properties.invType,
                                                            )
                                                        }
                                                    >
                                                        <PrintIcon />
                                                    </Link>
                                                )}
                                            </StyledWrapper>
                                            {checkifSelected(invoice.invoiceNumber) &&
                                                invoicesToPay.length &&
                                                Number(invoice.currentBalance) >= 0 ? (
                                                <StyledWrapper {...styles.mobilecurrentBalColumnBody}>
                                                    <StyledWrapper {...styles.mtPriceChangeContainer}>
                                                        <Typography as="span">$</Typography>
                                                        <TextField
                                                            type="number"
                                                            defaultValue={invoice.currentBalance}
                                                            value={showInvoicePaymentPrice(invoice.invoiceNumber)}
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                                upateInvoiceAmount(
                                                                    event,
                                                                    invoice.invoiceNumber,
                                                                    invoice.currentBalance,
                                                                )
                                                            }
                                                            onBlur={() => showInvoicePaymentPriceInfo()}
                                                        />
                                                        <Typography
                                                            {...styles.toolTip}
                                                            className="tooltip center-align"
                                                        >
                                                            {translate(
                                                                "Please enter the amount you would like to pay towards this invoice",
                                                            )}
                                                        </Typography>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            ) : Number(invoice.currentBalance) >= 0 ? (
                                                invoice.currentBalanceDisplay
                                            ) : (
                                                `(${invoice.currentBalanceDisplay.substring(1)})`
                                            )}
                                            <StyledWrapper
                                                {...styles.mtActionColumnBody}
                                                className={
                                                    getInvoiceParams.invoiceType === "0" ? "disabled" : "action-btn"
                                                }
                                            >
                                                {showInvoicePaymentPrice(invoice.invoiceNumber) >
                                                    invoice.currentBalance ? (
                                                    <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                        <StyledWrapper>
                                                            <ErrorCrossIcon />
                                                        </StyledWrapper>
                                                        <Typography {...styles.selectedInvoiceInfoMsg}>
                                                            {translate("Amount cannot be more than balance")}
                                                        </Typography>
                                                        <StyledWrapper
                                                            onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                        >
                                                            <CrossIcon />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                ) : checkifSelected(invoice.invoiceNumber) &&
                                                    showInvoicePaymentPrice(invoice.invoiceNumber) < 0 ? (
                                                    <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                        <Typography>
                                                            {translate("You will apply this amount toward your total.")}
                                                        </Typography>
                                                        <StyledWrapper
                                                            onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                        >
                                                            <CrossIcon />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                ) : showInvoicePaymentPrice(invoice.invoiceNumber) === 0 ? (
                                                    <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                        <StyledWrapper>
                                                            <ErrorCrossIcon />
                                                        </StyledWrapper>
                                                        <Typography>{translate("Amount cannot be 0")}</Typography>
                                                        <StyledWrapper
                                                            onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                        >
                                                            <CrossIcon />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                ) : checkifSelected(invoice.invoiceNumber) ? (
                                                    <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                        <StyledWrapper
                                                            {...styles.mtDropdownContainer}
                                                            className={
                                                                showInvoicePaymentPrice(invoice.invoiceNumber) ===
                                                                    invoice.currentBalance
                                                                    ? "disabled"
                                                                    : "normal"
                                                            }
                                                        >
                                                            <StyledWrapper
                                                                {...styles.dropdownValue}
                                                                onClick={() =>
                                                                    handleDropdownToggle(invoice.invoiceNumber)
                                                                }
                                                                className={
                                                                    openDropdowns[invoice.invoiceNumber]
                                                                        ? "open"
                                                                        : "closed"
                                                                }
                                                            >
                                                                {reasonCodes
                                                                    .find(
                                                                        code =>
                                                                            code.ReasonCode ===
                                                                            displayReasonCode(invoice.invoiceNumber),
                                                                    )
                                                                    ?.ReasonCodeDescription.toLowerCase() || "Reason"}

                                                                <Typography {...styles.mtIcon}>
                                                                    <ChevronDown />
                                                                </Typography>
                                                            </StyledWrapper>
                                                            {openDropdowns[invoice.invoiceNumber] ? (
                                                                <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                                                    <StyledWrapper
                                                                        {...styles.dropdownValues}
                                                                        as={"li"}
                                                                        onClick={() =>
                                                                            handleReasonCode(
                                                                                "00",
                                                                                invoice.invoiceNumber,
                                                                            )
                                                                        }
                                                                    >
                                                                        {translate("Reason")}
                                                                    </StyledWrapper>
                                                                    {reasonCodes.length
                                                                        ? reasonCodes.map(codes => {
                                                                            return (
                                                                                <StyledWrapper
                                                                                    {...styles.dropdownValues}
                                                                                    as={"li"}
                                                                                    onClick={() =>
                                                                                        handleReasonCode(
                                                                                            codes.ReasonCode,
                                                                                            invoice.invoiceNumber,
                                                                                        )
                                                                                    }
                                                                                    className={
                                                                                        reasonCodes
                                                                                            .find(
                                                                                                code =>
                                                                                                    code.ReasonCode ===
                                                                                                    displayReasonCode(
                                                                                                        invoice.invoiceNumber,
                                                                                                    ),
                                                                                            )
                                                                                            ?.ReasonCodeDescription.toLowerCase() ===
                                                                                            codes.ReasonCodeDescription.toLowerCase()
                                                                                            ? "active"
                                                                                            : " normal"
                                                                                    }
                                                                                >
                                                                                    {codes.ReasonCodeDescription.toLowerCase()}
                                                                                </StyledWrapper>
                                                                            );
                                                                        })
                                                                        : null}
                                                                </StyledWrapper>
                                                            ) : null}
                                                        </StyledWrapper>
                                                        <StyledWrapper
                                                            onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                        >
                                                            <CrossIcon />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                ) : // commented code P21 condition
                                                    // ) : (getInvoiceParams.customerSequence !== "ALLInv" &&
                                                    //       customerSequenceMatchesBillTo) ||
                                                    //   (getInvoiceParams.customerSequence === "ALLInv" &&
                                                    //       invoice.properties.showInvPrintPayLink === "true" &&
                                                    //       getInvoiceParams.invoiceType !== "0") ? (
                                                    getInvoiceParams.invoiceType === "1" ? (
                                                        showPayButton(invoice) &&
                                                            invoice.currentBalance > 0 &&
                                                            invoice.properties?.isDuplicateInvoicePayment === undefined ? (
                                                            <span onClick={() => onCheckboxChange(invoice.invoiceNumber)}>
                                                                {translate("Pay")}
                                                            </span>
                                                        ) : showApplyButton(invoice) &&
                                                            invoice.properties?.isDuplicateInvoicePayment === undefined ? (
                                                            <span onClick={() => onCheckboxChange(invoice.invoiceNumber)}>
                                                                {translate("Apply")}
                                                            </span>
                                                        ) : (
                                                            invoice.properties?.isDuplicateInvoicePayment === "true" && (
                                                                <span style={{ cursor: "text" }}>
                                                                    {translate("Payment Pending")}
                                                                </span>
                                                            )
                                                        )
                                                    ) : getInvoiceParams.invoiceType === "0" ? (
                                                        <span> {translate("Pay")} </span>
                                                    ) : null}
                                            </StyledWrapper>
                                        </GridItem>
                                    </GridContainer>
                                );
                            })}
                        </GridItem>
                    </GridContainer>
                    {finalinvoicesToPay.length && getInvoiceParams.invoiceType !== "0" ? (
                        <>
                            <StyledWrapper {...styles.mtPaymentTotal}>
                                <StyledWrapper>
                                    <StyledWrapper {...styles.mtPaymentInfoContainer}>
                                        <StyledWrapper {...styles.mtPaymentInfoContainerInner}>
                                            <Typography {...styles.mtPaymentTitle}>
                                                {translate("Payment Total:")}
                                                <Typography {...styles.payTotal}>
                                                    {allInvoicesTotal(finalinvoicesToPay) >= 0
                                                        ? `$${allInvoicesTotal(
                                                            finalinvoicesToPay.filter(
                                                                fv =>
                                                                    fv.properties.isDuplicateInvoicePayment !==
                                                                    "true",
                                                            ),
                                                        ).toFixed(2)}`
                                                        : `($${allInvoicesTotal(
                                                            finalinvoicesToPay.filter(
                                                                fv =>
                                                                    fv.properties.isDuplicateInvoicePayment !==
                                                                    "true",
                                                            ),
                                                        )
                                                            .toFixed(2)
                                                            .substring(1)})`}
                                                </Typography>
                                            </Typography>
                                            <Button
                                                {...styles.mtPaymentBtn}
                                                disabled={
                                                    allInvoicesTotal(finalinvoicesToPay) < 0 ||
                                                    showAllowInvoicePayment == false
                                                }
                                                onClick={updatePaymentHandler}
                                            >
                                                {translate("Payment Recap")}
                                            </Button>
                                        </StyledWrapper>

                                        {allInvoicesTotal(finalinvoicesToPay) < 0 ? (
                                            <StyledWrapper {...styles.warningInfoContainer}>
                                                <StyledWrapper {...styles.warningInfoIcon}>
                                                    <WarningIcon />
                                                </StyledWrapper>
                                                <Typography {...styles.mtWarningInfoText}>
                                                    {translate(
                                                        "To redeem a credit memo, the total value of the invoice must be greater than or equal to the value of the credit memo.",
                                                    )}
                                                </Typography>
                                            </StyledWrapper>
                                        ) : null}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </>
                    ) : null}
                </>
            ) : isTab ? (
                <>
                    <GridContainer {...styles.exportContainer}>
                        <GridItem {...styles.searchResultGridItem}>
                            <Typography {...styles.searchResult}>{translate(`Open Invoices`)}</Typography>
                            <Typography {...styles.pageresults}>
                                {translate(`Showing ${start} - ${end}  of ${invoiceData.pagination.totalItemCount}`)}
                            </Typography>
                            <Button shape="rounded" {...styles.exportImportButton} onClick={handleExport}>
                                <Icon src={ExportIcon} color="primary" {...styles.Icons} />
                                {translate("Export")}
                            </Button>
                            {/* Commented the below code since Import functionality is not implemented in US Spire: As per NA Spire Prod. */}
                            {/* <Button shape="rounded" {...styles.exportImportButton} onClick={handleImport}>
                                <Icon src={ImportIcon} color="primary" {...styles.Icons} />
                                {translate("Import")}
                            </Button> */}
                        </GridItem>
                        {showPageSizes ? (
                            <GridItem {...styles.selectGridItem}>
                                <Select
                                    className="pageSize"
                                    {...styles.select}
                                    label={translate("Per Page")}
                                    value={invoiceData.pagination.pageSize}
                                    labelPosition="left"
                                    onChange={changeResultsPerPage}
                                    data-test-selector="productListSortSelect"
                                >
                                    {invoiceData.pagination.pageSizeOptions
                                        .filter(option => option <= 36)
                                        .map(options => (
                                            <option key={options} value={options}>
                                                {options}
                                            </option>
                                        ))}
                                </Select>
                            </GridItem>
                        ) : null}
                    </GridContainer>
                    <GridContainer {...styles.container}>
                        <GridItem
                            {...styles.GridItemHeaderTab}
                            className={
                                getInvoiceParams.sort === "dpd,dueDate DESC"
                                    ? "ASC"
                                    : getInvoiceParams?.sort?.includes("DESC")
                                        ? "DESC"
                                        : "ASC"
                            }
                        >
                            <Hidden below="lg">
                                {isAllSelected ? (
                                    <LazyImage
                                        data-test-selector="isAllSelected"
                                        {...styles.checkboxImg}
                                        onClick={() => selectAllChangeHandler(!isAllSelected)}
                                        src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/MyListcheckbox-selected.svg"
                                    />
                                ) : atleastOneIsSelected ? (
                                    <LazyImage
                                        data-test-selector="atleastOneIsSelected"
                                        {...styles.checkboxImg}
                                        onClick={() => selectAllChangeHandler(!isAllSelected)}
                                        src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/MyListcheckbox-intermediate.svg"
                                    />
                                ) : (
                                    showSelectAllCheckBox && (
                                        <Checkbox
                                            {...styles.selectAll}
                                            checked={isAllSelected}
                                            onChange={selectAllChangeHandlerCheckBox}
                                            disabled={getInvoiceParams.invoiceType === "0"}
                                            data-test-selector="selectAllItems"
                                        />
                                    )
                                )}
                            </Hidden>

                            <StyledWrapper
                                {...styles.tabDateColumn}
                                onClick={() => handleSort("invoiceDate")}
                                className={getInvoiceParams.sort?.includes("invoiceDate") ? "sort-active" : "sort"}
                            >
                                <span>
                                    <SortUpArrow />
                                    {translate("Date")}
                                </span>
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.tabTypeColumn}
                                onClick={() => handleSort("invoiceType")}
                                className={getInvoiceParams.sort?.includes("invoiceType") ? "sort-active" : "sort"}
                            >
                                <span className="typ">
                                    <SortUpArrow />
                                    {translate("Type")}
                                    {getInvoiceParams.invoiceType === "1" ? (
                                        <Typography
                                            {...styles.tabFilterType}
                                            as="span"
                                            onClick={(e: React.MouseEvent) => handleOpenFilter(e)}
                                        ></Typography>
                                    ) : null}
                                </span>
                                {openFilterType ? (
                                    <StyledWrapper
                                        {...styles.FilterTypeContainer}
                                        className={
                                            getInvoiceParams.customerSequence === "ALLInv" ? "all-loc-active" : "normal"
                                        }
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    >
                                        <StyledWrapper {...styles.FilterTypeheading}>
                                            <Typography>{translate("Type Filter")}</Typography>
                                            <Typography onClick={(e: React.MouseEvent) => handleOpenFilter(e)}>
                                                <CrossIcon />
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.TypeFilterContainer}>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceType(e, "0")}
                                                className={
                                                    getInvoiceParams.invoiceFilterData === "0" ? "active" : "normal"
                                                }
                                            >
                                                {translate("All")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceType(e, "Invoice")}
                                                aria-disabled={
                                                    getInvoiceParams.invoiceFilterData === "Credit Memo" ? true : false
                                                }
                                                className={
                                                    getInvoiceParams.invoiceFilterData === "Invoice"
                                                        ? "active"
                                                        : "normal"
                                                }
                                            >
                                                {translate("Invoice")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceType(e, "Credit Memo")}
                                                aria-disabled={
                                                    disableTypeCM === "True"
                                                        ? true
                                                        : getInvoiceParams.invoiceFilterData === "Invoice"
                                                            ? true
                                                            : false
                                                }
                                                className={
                                                    getInvoiceParams.invoiceFilterData === "Credit Memo"
                                                        ? "active"
                                                        : "normal"
                                                }
                                            >
                                                {translate("Credit Memo")}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ) : null}
                            </StyledWrapper>
                            {getInvoiceParams.customerSequence === "ALLInv" ? (
                                <StyledWrapper
                                    {...styles.tabInvoiceColumn}
                                    onClick={() => handleSort("customerNumber")}
                                    className={
                                        getInvoiceParams.sort?.includes("customerNumber") ? "sort-active" : "sort"
                                    }
                                >
                                    <span>
                                        <SortUpArrow />
                                        {translate("Loc")}
                                    </span>
                                </StyledWrapper>
                            ) : null}
                            <StyledWrapper
                                {...styles.tabInvoiceColumn}
                                onClick={() => handleSort("invoiceNumber")}
                                className={getInvoiceParams.sort?.includes("invoiceNumber") ? "sort-active" : "sort"}
                            >
                                <span>
                                    <SortUpArrow />
                                    {translate("Invoice")}
                                </span>
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.tabOrderColumn}
                                onClick={() => handleSort("orderNumber")}
                                className={getInvoiceParams.sort?.includes("orderNumber") ? "sort-active" : "sort"}
                            >
                                <span>
                                    <SortUpArrow />
                                    {translate("Order #")}
                                </span>
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.tabPoColumn}
                                onClick={() => handleSort("customerPO")}
                                className={getInvoiceParams.sort?.includes("customerPO") ? "sort-active" : "sort"}
                            >
                                <span>
                                    <SortUpArrow />
                                    {translate("PO #")}
                                </span>
                            </StyledWrapper>

                            <StyledWrapper
                                {...styles.tabDueDateColumn}
                                onClick={() => handleSort("dueDate")}
                                className={
                                    getInvoiceParams.sort === "dueDate" || getInvoiceParams.sort === "dueDate DESC"
                                        ? "sort-active"
                                        : "sort"
                                }
                            >
                                <span className="typ">
                                    <SortUpArrow />
                                    {translate("Due Date ")}
                                    {getInvoiceParams.invoiceType === "1" ? (
                                        <Typography
                                            {...styles.FilterDPD}
                                            onClick={(e: React.MouseEvent) => handleOpenDPDFilter(e)}
                                        ></Typography>
                                    ) : null}
                                </span>
                                {openDPDFilterType ? (
                                    <StyledWrapper
                                        {...styles.FilterDPDContainer}
                                        className={
                                            getInvoiceParams.customerSequence === "ALLInv" ? "all-loc-active" : "normal"
                                        }
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    >
                                        <StyledWrapper {...styles.FilterTypeheading}>
                                            <Typography>{translate("Days Past Due Filter")}</Typography>
                                            <Typography onClick={(e: React.MouseEvent) => handleOpenDPDFilter(e)}>
                                                <CrossIcon />
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.TypeFilterContainer}>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceDPDType(e, "0")}
                                                className={getInvoiceParams.dpdFilterData === "0" ? "active" : "normal"}
                                            >
                                                {translate("All")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceDPDType(e, "1-30")}
                                                aria-disabled={DPD1to30 === "True" ? true : false}
                                                className={
                                                    getInvoiceParams.dpdFilterData === "1-30" ? "active" : "normal"
                                                }
                                            >
                                                {translate("1-30")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceDPDType(e, "31-60")}
                                                aria-disabled={DPD31to60 === "True" ? true : false}
                                                className={
                                                    getInvoiceParams.dpdFilterData === "31-60" ? "active" : "normal"
                                                }
                                            >
                                                {translate("31-60")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceDPDType(e, "61-90")}
                                                aria-disabled={DPD61to90 === "True" ? true : false}
                                                className={
                                                    getInvoiceParams.dpdFilterData === "61-90" ? "active" : "normal"
                                                }
                                            >
                                                {translate("61-90")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceDPDType(e, "91")}
                                                aria-disabled={DPDover90 === "True" ? true : false}
                                                className={
                                                    getInvoiceParams.dpdFilterData === "91" ? "active" : "normal"
                                                }
                                            >
                                                {translate("91+")}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ) : null}
                            </StyledWrapper>
                            <Hidden below="lg">
                                <StyledWrapper
                                    {...styles.dpdColumn}
                                    onClick={() => handleSort("dpd,dueDate DESC")}
                                    className={getInvoiceParams.sort?.includes("dpd") ? "sort-active" : "sort"}
                                >
                                    {getInvoiceParams.invoiceType === "1" && (
                                        <span>
                                            <SortUpArrow />
                                            {translate("DPD")}

                                            <Typography {...styles.tabFilterType}></Typography>
                                        </span>
                                    )}
                                </StyledWrapper>
                            </Hidden>
                            <StyledWrapper
                                {...styles.tabCurrentBalColumn}
                                onClick={() => handleSort("CurrentBalance")}
                                className={getInvoiceParams.sort?.includes("CurrentBalance") ? "sort-active" : "sort"}
                            >
                                <span>
                                    <SortUpArrow />
                                    {translate("Balance")}
                                </span>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.GridItemInvoices}>
                            {invoiceData.invoices.map((invoice, index: number) => {
                                const customerSequenceMatchesBillTo = checkCustomerSequenceMatchesBillTo(
                                    invoice.customerNumber,
                                    invoice.invoiceType,
                                );
                                const formattedDate = invoice.invoiceDate
                                    ? new Date(invoice.invoiceDate).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                    })
                                    : "";
                                const dotAddedDate = formatedDateWithDot(formattedDate);
                                const formattedDueDate = invoice.invoiceDate
                                    ? new Date(invoice.invoiceDate).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                    })
                                    : "";

                                return (
                                    <>
                                        <GridItem
                                            {...styles.tabSingleInvoiceGridItem}
                                            as="li"
                                            key={`Item${index}`}
                                            className={
                                                checkifSelected(invoice.invoiceNumber) ? "selected" : "non-selected"
                                            }
                                        >
                                            <Hidden below="lg">
                                                {getInvoiceParams.customerSequence === "ALLInv"
                                                    ? invoice.properties.showInvPrintPayLink.toLowerCase() ===
                                                    "true" && (
                                                        <Checkbox
                                                            {...styles.selectAll}
                                                            checked={checkboxStatus[invoice.invoiceNumber.trim()]}
                                                            onChange={() => onCheckboxChange(invoice.invoiceNumber)}
                                                            data-test-selector="selectSingleItems"
                                                            disabled={
                                                                invoice.otherCharges < 0 ||
                                                                getInvoiceParams.invoiceType === "0"
                                                            }
                                                        />
                                                    )
                                                    : customerSequenceMatchesBillTo && (
                                                        <Checkbox
                                                            {...styles.selectAll}
                                                            checked={checkboxStatus[invoice.invoiceNumber.trim()]}
                                                            onChange={() => onCheckboxChange(invoice.invoiceNumber)}
                                                            data-test-selector="selectSingleItems"
                                                            disabled={
                                                                invoice.otherCharges < 0 ||
                                                                getInvoiceParams.invoiceType === "0"
                                                            }
                                                        />
                                                    )}
                                            </Hidden>
                                            <StyledWrapper {...styles.tabDateColumnBody}>{dotAddedDate}</StyledWrapper>
                                            <StyledWrapper {...styles.tabInvoiceLinkBody}>
                                                <span>{invoice.invoiceType}</span>
                                            </StyledWrapper>
                                            {getInvoiceParams.customerSequence === "ALLInv" ? (
                                                <StyledWrapper {...styles.tabPoColumnBody}>
                                                    <span>{invoice.customerNumber.split("-")[0]}</span>
                                                </StyledWrapper>
                                            ) : null}
                                            <StyledWrapper {...styles.tabInvoiceLinkBody}>
                                                {customerSequenceMatchesBillTo ? (
                                                    <InvoiceDetailPageTypeLink
                                                        title={invoice.invoiceNumber}
                                                        invoiceNumber={invoice.invoiceNumber}
                                                        invoiceType={invoice.properties.invType}
                                                    />
                                                ) : (
                                                    <Typography {...styles.invoiceNumberNonLink}>
                                                        {invoice.invoiceNumber}
                                                    </Typography>
                                                )}
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.tabPoColumnBody}>
                                                {invoice.properties.orderNumber}
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.tabPoColumnBody}>
                                                {invoice.customerPO}
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.tabDueDateColumnBody}>
                                                {(invoice.currentBalance > 0 || getInvoiceParams.invoiceType === "0") &&
                                                    invoice.dueDate !== "-1"
                                                    ? formatYearData(invoice.dueDate)
                                                    : null}
                                            </StyledWrapper>
                                            <Hidden below="lg">
                                                <StyledWrapper {...styles.dpdColumnBody}>
                                                    {getInvoiceParams.invoiceType === "1" &&
                                                        invoice.currentBalance > 0 &&
                                                        invoice.notes !== "-1" &&
                                                        (invoice.notes === "0"
                                                            ? translate("Current")
                                                            : invoice.notes === "-1"
                                                                ? null
                                                                : `${invoice.notes}`)}
                                                </StyledWrapper>
                                            </Hidden>
                                            <StyledWrapper {...styles.tabCurrentBalColumnBody}>
                                                {/* <Typography as="span">$</Typography> */}
                                                {Number(invoice.currentBalance) >= 0
                                                    ? invoice.currentBalanceDisplay
                                                    : `(${invoice.currentBalanceDisplay.substring(1)})`}
                                            </StyledWrapper>
                                        </GridItem>
                                        <GridItem {...styles.tabGridItem} as="li">
                                            <StyledWrapper {...styles.tabPrint}>
                                                {showPrintIcon(invoice) && (
                                                    <Link
                                                        {...styles.mtPrintButton}
                                                        onClick={() =>
                                                            onClickPrint(
                                                                invoice.invoiceNumber,
                                                                invoice.properties.invType,
                                                            )
                                                        }
                                                    >
                                                        <PrintIcon />
                                                    </Link>
                                                )}
                                            </StyledWrapper>
                                            {checkifSelected(invoice.invoiceNumber) &&
                                                invoicesToPay.length &&
                                                Number(invoice.currentBalance) >= 0 ? (
                                                <StyledWrapper>
                                                    <StyledWrapper {...styles.mtPriceChangeContainer}>
                                                        <Typography as="span">$</Typography>
                                                        <TextField
                                                            type="number"
                                                            defaultValue={invoice.currentBalance}
                                                            value={showInvoicePaymentPrice(invoice.invoiceNumber)}
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                                upateInvoiceAmount(
                                                                    event,
                                                                    invoice.invoiceNumber,
                                                                    invoice.currentBalance,
                                                                )
                                                            }
                                                            onBlur={() => showInvoicePaymentPriceInfo()}
                                                            {...styles.tabbularCurrentBalColumnBody}
                                                        />
                                                        <Typography
                                                            {...styles.toolTip}
                                                            className="tooltip center-align"
                                                        >
                                                            {translate(
                                                                "Please enter the amount you would like to pay towards this invoice",
                                                            )}
                                                        </Typography>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            ) : null}
                                            <StyledWrapper
                                                {...styles.tabActionColumnBody}
                                                className={
                                                    getInvoiceParams.invoiceType === "0" ? "disabled" : "action-btn"
                                                }
                                            >
                                                {showInvoicePaymentPrice(invoice.invoiceNumber) >
                                                    invoice.currentBalance ? (
                                                    <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                        <StyledWrapper>
                                                            <ErrorCrossIcon />
                                                        </StyledWrapper>
                                                        <Typography {...styles.selectedInvoiceInfoMsg}>
                                                            {translate("Amount cannot be more than balance")}
                                                        </Typography>
                                                        <StyledWrapper
                                                            onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                        >
                                                            <CrossIcon />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                ) : checkifSelected(invoice.invoiceNumber) &&
                                                    showInvoicePaymentPrice(invoice.invoiceNumber) < 0 ? (
                                                    <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                        <Typography>
                                                            {translate("You will apply this amount toward your total.")}
                                                        </Typography>
                                                        <StyledWrapper
                                                            onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                        >
                                                            <CrossIcon />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                ) : showInvoicePaymentPrice(invoice.invoiceNumber) === 0 ? (
                                                    <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                        <StyledWrapper>
                                                            <ErrorCrossIcon />
                                                        </StyledWrapper>
                                                        <Typography>{translate("Amount cannot be 0")}</Typography>
                                                        <StyledWrapper
                                                            onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                        >
                                                            <CrossIcon />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                ) : checkifSelected(invoice.invoiceNumber) ? (
                                                    <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                        <StyledWrapper
                                                            {...styles.mtDropdownContainer}
                                                            className={
                                                                showInvoicePaymentPrice(invoice.invoiceNumber) ===
                                                                    invoice.currentBalance
                                                                    ? "disabled"
                                                                    : "normal"
                                                            }
                                                        >
                                                            <StyledWrapper
                                                                {...styles.dropdownValue}
                                                                onClick={() =>
                                                                    handleDropdownToggle(invoice.invoiceNumber)
                                                                }
                                                                className={
                                                                    openDropdowns[invoice.invoiceNumber]
                                                                        ? "open"
                                                                        : "closed"
                                                                }
                                                            >
                                                                {reasonCodes
                                                                    .find(
                                                                        code =>
                                                                            code.ReasonCode ===
                                                                            displayReasonCode(invoice.invoiceNumber),
                                                                    )
                                                                    ?.ReasonCodeDescription.toLowerCase() || "Reason"}

                                                                <Typography {...styles.mtIcon}>
                                                                    <ChevronDown />
                                                                </Typography>
                                                            </StyledWrapper>
                                                            {openDropdowns[invoice.invoiceNumber] ? (
                                                                <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                                                    <StyledWrapper
                                                                        {...styles.dropdownValues}
                                                                        as={"li"}
                                                                        onClick={() =>
                                                                            handleReasonCode(
                                                                                "00",
                                                                                invoice.invoiceNumber,
                                                                            )
                                                                        }
                                                                    >
                                                                        {translate("Reason")}
                                                                    </StyledWrapper>
                                                                    {reasonCodes.length
                                                                        ? reasonCodes.map(codes => {
                                                                            return (
                                                                                <StyledWrapper
                                                                                    {...styles.dropdownValues}
                                                                                    as={"li"}
                                                                                    onClick={() =>
                                                                                        handleReasonCode(
                                                                                            codes.ReasonCode,
                                                                                            invoice.invoiceNumber,
                                                                                        )
                                                                                    }
                                                                                    className={
                                                                                        reasonCodes
                                                                                            .find(
                                                                                                code =>
                                                                                                    code.ReasonCode ===
                                                                                                    displayReasonCode(
                                                                                                        invoice.invoiceNumber,
                                                                                                    ),
                                                                                            )
                                                                                            ?.ReasonCodeDescription.toLowerCase() ===
                                                                                            codes.ReasonCodeDescription.toLowerCase()
                                                                                            ? "active"
                                                                                            : " normal"
                                                                                    }
                                                                                >
                                                                                    {codes.ReasonCodeDescription.toLowerCase()}
                                                                                </StyledWrapper>
                                                                            );
                                                                        })
                                                                        : null}
                                                                </StyledWrapper>
                                                            ) : null}
                                                        </StyledWrapper>
                                                        <StyledWrapper
                                                            onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                        >
                                                            <CrossIcon />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                ) : getInvoiceParams.invoiceType === "1" ? (
                                                    showPayButton(invoice) &&
                                                        // commented code P21
                                                        // ) :
                                                        // (getInvoiceParams.customerSequence !== "ALLInv" &&
                                                        //       customerSequenceMatchesBillTo) ||
                                                        //   (getInvoiceParams.customerSequence === "ALLInv" &&
                                                        //       invoice.properties.showInvPrintPayLink === "true" &&
                                                        //       getInvoiceParams.invoiceType !== "0") ? (
                                                        invoice.currentBalance > 0 &&
                                                        invoice.properties?.isDuplicateInvoicePayment === undefined ? (
                                                        <span onClick={() => onCheckboxChange(invoice.invoiceNumber)}>
                                                            {translate("Pay")}
                                                        </span>
                                                    ) : showApplyButton(invoice) &&
                                                        invoice.properties?.isDuplicateInvoicePayment === undefined ? (
                                                        <span onClick={() => onCheckboxChange(invoice.invoiceNumber)}>
                                                            {translate("Apply")}
                                                        </span>
                                                    ) : (
                                                        invoice.properties?.isDuplicateInvoicePayment === "true" && (
                                                            <span style={{ cursor: "text" }}>
                                                                {translate("Payment Pending")}
                                                            </span>
                                                        )
                                                    )
                                                ) : getInvoiceParams.invoiceType === "0" ? (
                                                    <span> {translate("Pay")} </span>
                                                ) : null}
                                            </StyledWrapper>
                                        </GridItem>
                                    </>
                                );
                            })}
                        </GridItem>
                    </GridContainer>
                    {finalinvoicesToPay.length && selectedCount && getInvoiceParams.invoiceType !== "0" ? (
                        <>
                            {
                                <StyledWrapper {...styles.paymentTotal}>
                                    <Typography>
                                        {/* {translate("You have selected {0} invoice.", `${finalinvoicesToPay.length}`)} */}
                                        {translate(
                                            `You have selected ${finalinvoicesToPay.length -
                                            finalinvoicesToPay.filter(
                                                fv => fv.properties.isDuplicateInvoicePayment === "true",
                                            ).length
                                            } invoice${finalinvoicesToPay.length > 1 ? "s" : ""}.`,
                                        )}
                                    </Typography>
                                    <StyledWrapper>
                                        <StyledWrapper {...styles.paymentInfoContainer}>
                                            <Typography {...styles.paymentTitle}>
                                                {translate("Payment Total:")}
                                                <Typography {...styles.payTotal}>
                                                    {allInvoicesTotal(finalinvoicesToPay) >= 0
                                                        ? `$${allInvoicesTotal(
                                                            finalinvoicesToPay.filter(
                                                                fv =>
                                                                    fv.properties.isDuplicateInvoicePayment !==
                                                                    "true",
                                                            ),
                                                        ).toFixed(2)}`
                                                        : `($${allInvoicesTotal(
                                                            finalinvoicesToPay.filter(
                                                                fv =>
                                                                    fv.properties.isDuplicateInvoicePayment !==
                                                                    "true",
                                                            ),
                                                        )
                                                            .toFixed(2)
                                                            .substring(1)})`}
                                                </Typography>
                                            </Typography>
                                            <Button
                                                {...styles.paymentBtn}
                                                disabled={
                                                    allInvoicesTotal(finalinvoicesToPay) < 0 ||
                                                    showAllowInvoicePayment == false
                                                }
                                                onClick={updatePaymentHandler}
                                            >
                                                {translate("Payment Recap")}
                                            </Button>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            }
                            {allInvoicesTotal(finalinvoicesToPay) < 0 ? (
                                <StyledWrapper {...styles.warningInfoContainer}>
                                    <StyledWrapper {...styles.warningInfoIcon}>
                                        <WarningIcon />
                                    </StyledWrapper>
                                    <Typography {...styles.warningInfoText}>
                                        {translate(
                                            "To redeem a credit memo, the total value of the invoice must be greater than or equal to the value of the credit memo.",
                                        )}
                                    </Typography>
                                </StyledWrapper>
                            ) : null}
                        </>
                    ) : null}
                </>
            ) : (
                <>
                    <GridContainer {...styles.exportContainer}>
                        <GridItem {...styles.searchResultGridItem}>
                            <Typography {...styles.searchResult}>{translate(`Open Invoices`)}</Typography>
                            <Typography {...styles.pageresults}>
                                {translate(`Showing ${start} - ${end}  of ${formattedTotalItemCountValue}`)}
                            </Typography>
                            <Button shape="rounded" {...styles.exportImportButton} onClick={handleExport}>
                                <Icon src={ExportIcon} color="primary" {...styles.Icons} />
                                {translate("Export")}
                            </Button>
                            {/* Commented the below code since Import functionality is not implemented in US Spire: As per NA Spire Prod. */}
                            {/* <Button shape="rounded" {...styles.exportImportButton} onClick={handleImport}>
                                <Icon src={ImportIcon} color="primary" {...styles.Icons} />
                                {translate("Import")}
                            </Button> */}
                        </GridItem>
                        {showPageSizes ? (
                            <GridItem {...styles.selectGridItem}>
                                <Select
                                    className="pageSize"
                                    {...styles.select}
                                    label={translate("Per Page")}
                                    value={invoiceData.pagination.pageSize}
                                    labelPosition="left"
                                    onChange={changeResultsPerPage}
                                    data-test-selector="productListSortSelect"
                                >
                                    {invoiceData.pagination.pageSizeOptions
                                        .filter(option => option <= 36)
                                        .map(options => (
                                            <option key={options} value={options}>
                                                {options}
                                            </option>
                                        ))}
                                </Select>
                            </GridItem>
                        ) : null}
                    </GridContainer>
                    <GridContainer {...styles.container}>
                        <GridItem
                            {...styles.GridItemHeader}
                            className={
                                getInvoiceParams.sort === "dpd,dueDate DESC"
                                    ? "ASC"
                                    : getInvoiceParams?.sort?.includes("DESC")
                                        ? "DESC"
                                        : "ASC"
                            }
                        >
                            <Hidden below="lg" className="viewinvoicedatecheckbox">
                                {isAllSelected ? (
                                    <LazyImage
                                        {...styles.checkboxImg}
                                        data-test-selector="isAllSelected"
                                        onClick={() => selectAllChangeHandler(!isAllSelected)}
                                        src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/MyListcheckbox-selected.svg"
                                    />
                                ) : atleastOneIsSelected ? (
                                    <LazyImage
                                        {...styles.checkboxImg}
                                        data-test-selector="atleastOneIsSelected"
                                        onClick={() => selectAllChangeHandler(!isAllSelected)}
                                        src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/MyListcheckbox-intermediate.svg"
                                    />
                                ) : (
                                    showSelectAllCheckBox && (
                                        <Checkbox
                                            {...styles.selectAll}
                                            checked={isAllSelected}
                                            onChange={selectAllChangeHandlerCheckBox}
                                            disabled={getInvoiceParams.invoiceType === "0"}
                                            data-test-selector="selectAllItems"
                                        />
                                    )
                                )}
                            </Hidden>

                            <StyledWrapper
                                {...styles.dateColumn}
                                onClick={() => handleSort("invoiceDate")}
                                className={
                                    getInvoiceParams.sort?.includes("invoiceDate")
                                        ? "sort-active viewinvoicedate"
                                        : "sort viewinvoicedate "
                                }
                            >
                                <span>
                                    <SortUpArrow />
                                    {translate("Date")}
                                </span>
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.typeColumn}
                                onClick={() => handleSort("invoiceType")}
                                className={
                                    getInvoiceParams.sort?.includes("invoiceType")
                                        ? "sort-active viewinvoicetype "
                                        : "sort viewinvoicetype"
                                }
                            >
                                <span className="typ">
                                    <SortUpArrow />
                                    {translate("Type")}
                                    {getInvoiceParams.invoiceType === "1" ? (
                                        <Typography
                                            {...styles.FilterType}
                                            as="span"
                                            onClick={(e: React.MouseEvent) => handleOpenFilter(e)}
                                        ></Typography>
                                    ) : null}
                                </span>
                                {openFilterType ? (
                                    <StyledWrapper
                                        {...styles.FilterTypeContainer}
                                        className={
                                            getInvoiceParams.customerSequence === "ALLInv"
                                                ? "all-loc-active viewinvoicelocat"
                                                : "normal viewinvoicelocat"
                                        }
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    >
                                        <StyledWrapper {...styles.FilterTypeheading}>
                                            <Typography>{translate("Type Filter")}</Typography>
                                            <Typography onClick={(e: React.MouseEvent) => handleOpenFilter(e)}>
                                                <CrossIcon />
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.TypeFilterContainer}>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceType(e, "0")}
                                                className={
                                                    getInvoiceParams.invoiceFilterData === "0" ? "active" : "normal"
                                                }
                                            >
                                                {translate("All")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceType(e, "Invoice")}
                                                aria-disabled={
                                                    getInvoiceParams.invoiceFilterData === "Credit Memo" ? true : false
                                                }
                                                className={
                                                    getInvoiceParams.invoiceFilterData === "Invoice"
                                                        ? "active "
                                                        : "normal "
                                                }
                                            >
                                                {translate("Invoice")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceType(e, "Credit Memo")}
                                                aria-disabled={
                                                    disableTypeCM === "True"
                                                        ? true
                                                        : getInvoiceParams.invoiceFilterData === "Invoice"
                                                            ? true
                                                            : false
                                                }
                                                className={
                                                    getInvoiceParams.invoiceFilterData === "Credit Memo"
                                                        ? "active"
                                                        : "normal"
                                                }
                                            >
                                                {translate("Credit Memo")}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ) : null}
                            </StyledWrapper>
                            {getInvoiceParams.customerSequence === "ALLInv" ? (
                                <StyledWrapper
                                    {...styles.invoiceColumn}
                                    onClick={() => handleSort("customerNumber")}
                                    className={
                                        getInvoiceParams.sort?.includes("customerNumber") ? "sort-active" : "sort"
                                    }
                                >
                                    <span>
                                        <SortUpArrow />
                                        {translate("Loc")}
                                    </span>
                                </StyledWrapper>
                            ) : null}
                            <StyledWrapper
                                {...styles.invoiceColumn}
                                onClick={() => handleSort("invoiceNumber")}
                                className={getInvoiceParams.sort?.includes("invoiceNumber") ? "sort-active " : "sort "}
                            >
                                <span className="typ">
                                    <SortUpArrow />
                                    {translate("Invoice")}
                                </span>
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.poColumn}
                                onClick={() => handleSort("orderNumber")}
                                className={getInvoiceParams.sort?.includes("orderNumber") ? "sort-active " : "sort "}
                            >
                                <span className="typ">
                                    <SortUpArrow />
                                    {translate("Order #")}
                                </span>
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.poColumn}
                                onClick={() => handleSort("customerPO")}
                                className={
                                    getInvoiceParams.sort?.includes("customerPO")
                                        ? "sort-active viewinvoicepo"
                                        : "sort viewinvoicepo"
                                }
                            >
                                <span>
                                    <SortUpArrow />
                                    {translate("PO #")}
                                </span>
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.dueDateColumn}
                                onClick={() => handleSort("dueDate")}
                                className={
                                    getInvoiceParams.sort === "dueDate" || getInvoiceParams.sort === "dueDate DESC"
                                        ? "sort-active viewinvoiceduedate"
                                        : "sort viewinvoiceduedate"
                                }
                            >
                                <span>
                                    <SortUpArrow />
                                    {translate("Due Date ")}
                                </span>
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.dpdColumn}
                                onClick={() => handleSort("dpd,dueDate DESC")}
                                className={
                                    getInvoiceParams.sort?.includes("dpd")
                                        ? "sort-active viewinvoicedpd"
                                        : "sort viewinvoicedpd"
                                }
                            >
                                {getInvoiceParams.invoiceType === "1" && (
                                    <span className="typ">
                                        <SortUpArrow />
                                        {translate("DPD")}
                                        {getInvoiceParams.invoiceType === "1" ? (
                                            <Typography
                                                {...styles.FilterDPD}
                                                onClick={(e: React.MouseEvent) => handleOpenDPDFilter(e)}
                                            ></Typography>
                                        ) : null}
                                    </span>
                                )}
                                {getInvoiceParams.invoiceType === "1" && openDPDFilterType ? (
                                    <StyledWrapper
                                        {...styles.FilterDPDContainer}
                                        className={
                                            getInvoiceParams.customerSequence === "ALLInv" ? "all-loc-active" : "normal"
                                        }
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    >
                                        <StyledWrapper {...styles.FilterTypeheading}>
                                            <Typography>{translate("Days Past Due Filter")}</Typography>
                                            <Typography onClick={(e: React.MouseEvent) => handleOpenDPDFilter(e)}>
                                                <CrossIcon />
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.TypeFilterContainer}>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceDPDType(e, "0")}
                                                className={getInvoiceParams.dpdFilterData === "0" ? "active" : "normal"}
                                            >
                                                {translate("All")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceDPDType(e, "1-30")}
                                                aria-disabled={DPD1to30 === "True" ? true : false}
                                                className={
                                                    getInvoiceParams.dpdFilterData === "1-30" ? "active" : "normal"
                                                }
                                            >
                                                {translate("1-30")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceDPDType(e, "31-60")}
                                                aria-disabled={DPD31to60 === "True" ? true : false}
                                                className={
                                                    getInvoiceParams.dpdFilterData === "31-60" ? "active" : "normal"
                                                }
                                            >
                                                {translate("31-60")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceDPDType(e, "61-90")}
                                                aria-disabled={DPD61to90 === "True" ? true : false}
                                                className={
                                                    getInvoiceParams.dpdFilterData === "61-90" ? "active" : "normal"
                                                }
                                            >
                                                {translate("61-90")}
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.tabs}
                                                onClick={(e: React.MouseEvent) => changeInvoiceDPDType(e, "91")}
                                                aria-disabled={DPDover90 === "True" ? true : false}
                                                className={
                                                    getInvoiceParams.dpdFilterData === "91" ? "active" : "normal"
                                                }
                                            >
                                                {translate("91+")}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ) : null}
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.currentBalColumn}
                                onClick={() => handleSort("CurrentBalance")}
                                className={
                                    getInvoiceParams.sort?.includes("CurrentBalance")
                                        ? "sort-active viewinvoiceheader"
                                        : "sort viewinvoiceheader"
                                }
                            >
                                <span>
                                    <SortUpArrow />
                                    {translate("Balance")}
                                </span>
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.actionColumn}
                                className={
                                    getInvoiceParams.invoiceType === "0"
                                        ? "disabled viewinvoiceactions"
                                        : "action-btn viewinvoiceactions"
                                }
                            >
                                <span>{translate("Actions")}</span>
                                {invoicesToPay.length && selectedCount ? (
                                    <StyledWrapper {...styles.actionsClose} onClick={deSeletAllInvoices}>
                                        <CrossIcon />
                                    </StyledWrapper>
                                ) : null}
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.GridItemInvoices}>
                            {invoiceData.invoices.map((invoice, index: number) => {
                                if (
                                    invoice.properties.isDuplicateInvoicePayment === "true" &&
                                    isDuplicatePaymentExists === 0
                                ) {
                                    isDuplicatePaymentExists += 1;
                                }
                                const customerSequenceMatchesBillTo = checkCustomerSequenceMatchesBillTo(
                                    invoice.customerNumber,
                                    invoice.invoiceType,
                                );
                                const formattedDate = invoice.invoiceDate
                                    ? new Date(invoice.invoiceDate).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                    })
                                    : "";
                                const dotAddedDate = formatedDateWithDot(formattedDate);
                                const formattedDueDate = invoice.invoiceDate
                                    ? new Date(invoice.invoiceDate).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                    })
                                    : "";

                                return (
                                    // <StyledWrapper className="invoicelistsection">
                                    <GridItem
                                        {...styles.singleInvoiceGridItem}
                                        as="li"
                                        key={`Item${index}`}
                                        className={
                                            checkifSelected(invoice.invoiceNumber) &&
                                                invoice.properties?.isDuplicateInvoicePayment !== "true"
                                                ? "selected"
                                                : "non-selected"
                                        }
                                    >
                                        <Hidden below="lg" className="viewinvoicecontentcheckbox">
                                            {getInvoiceParams.customerSequence === "ALLInv"
                                                ? invoice.properties.showInvPrintPayLink.toLowerCase() === "true" && (
                                                    <Checkbox
                                                        {...styles.selectAll}
                                                        checked={
                                                            invoice.properties?.isDuplicateInvoicePayment !==
                                                            "true" && checkboxStatus[invoice.invoiceNumber.trim()]
                                                        }
                                                        onChange={() => onCheckboxChange(invoice.invoiceNumber)}
                                                        data-test-selector="selectSingleItems"
                                                        disabled={
                                                            invoice.otherCharges < 0 ||
                                                            getInvoiceParams.invoiceType === "0"
                                                        }
                                                    />
                                                )
                                                : customerSequenceMatchesBillTo && (
                                                    <Checkbox
                                                        {...styles.selectAll}
                                                        checked={
                                                            invoice.properties?.isDuplicateInvoicePayment !==
                                                            "true" && checkboxStatus[invoice.invoiceNumber.trim()]
                                                        }
                                                        onChange={() =>
                                                            invoice.properties?.isDuplicateInvoicePayment ===
                                                                undefined
                                                                ? onCheckboxChange(invoice.invoiceNumber)
                                                                : null
                                                        }
                                                        data-test-selector="selectSingleItems"
                                                        disabled={
                                                            invoice.properties?.isDuplicateInvoicePayment ===
                                                            "true" ||
                                                            invoice.otherCharges < 0 ||
                                                            getInvoiceParams.invoiceType === "0"
                                                        }
                                                    />
                                                )}
                                        </Hidden>
                                        <StyledWrapper
                                            {...styles.dateColumnBody}
                                            className="viewinvoicecontentdotaddeddate"
                                        >
                                            {dotAddedDate}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.invoiceTypeBody}
                                            className="viewinvoicecontentinvoicetype"
                                        >
                                            <Typography
                                                className={
                                                    invoice.invoiceType === "Credit Memo" ||
                                                        invoice.invoiceType === "Total Credit"
                                                        ? "creditType"
                                                        : ""
                                                }
                                            >
                                                {invoice.invoiceType}
                                            </Typography>
                                        </StyledWrapper>
                                        {getInvoiceParams.customerSequence === "ALLInv" ? (
                                            <StyledWrapper {...styles.poColumnBody}>
                                                <span>{invoice?.customerNumber?.split("-")[1] ?? ""}</span>
                                            </StyledWrapper>
                                        ) : null}
                                        <StyledWrapper
                                            {...styles.invoiceLinkBody}
                                            className="viewinvoicecontentinvoicenumberprint"
                                        >
                                            {getInvoiceParams.customerSequence === "ALLInv" &&
                                                invoice.properties.showInvPrintPayLink.toLowerCase() === "true" ? (
                                                <InvoiceDetailPageTypeLink
                                                    title={invoice.invoiceNumber}
                                                    invoiceNumber={invoice.invoiceNumber}
                                                    invoiceType={invoice.properties.invType}
                                                />
                                            ) : customerSequenceMatchesBillTo ? (
                                                <InvoiceDetailPageTypeLink
                                                    title={invoice.invoiceNumber}
                                                    invoiceNumber={invoice.invoiceNumber}
                                                    invoiceType={invoice.properties.invType}
                                                />
                                            ) : (
                                                <Typography {...styles.invoiceNumberNonLink}>
                                                    {invoice.invoiceNumber}
                                                </Typography>
                                            )}
                                            {showPrintIcon(invoice) && (
                                                <Link
                                                    {...styles.printButton}
                                                    onClick={() =>
                                                        onClickPrint(invoice.invoiceNumber, invoice.properties.invType)
                                                    }
                                                >
                                                    <PrintIcon />
                                                </Link>
                                            )}
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.poColumnBody}>
                                            {invoice.properties.orderNumber}
                                        </StyledWrapper>
                                        <StyledWrapper className="viewinvoicecontentpo" {...styles.poColumnBody}>
                                            {invoice.customerPO}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.dueDateColumnBody}
                                            className="viewinvoicecontentduedate"
                                        >
                                            {(invoice.currentBalance > 0 || getInvoiceParams.invoiceType === "0") &&
                                                invoice.dueDate !== "-1"
                                                ? formatYearData(invoice.dueDate)
                                                : null}
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.dpdColumnBody} className="viewinvoicecontentdpd">
                                            {getInvoiceParams.invoiceType === "1" &&
                                                invoice.currentBalance > 0 &&
                                                invoice.notes !== "-1" &&
                                                (invoice.notes === "0"
                                                    ? translate("Current")
                                                    : invoice.notes === "-1"
                                                        ? null
                                                        : `${invoice.notes}`)}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.currentBalColumnBody}
                                            className="viewinvoicecontentbalance"
                                        >
                                            {invoice.properties?.isDuplicateInvoicePayment !== "true" &&
                                                checkifSelected(invoice.invoiceNumber) &&
                                                invoicesToPay.length &&
                                                Number(invoice.currentBalance) >= 0 ? (
                                                <StyledWrapper {...styles.priceChangeContainer}>
                                                    <Typography as="span">$</Typography>
                                                    <TextField
                                                        type="number"
                                                        defaultValue={invoice.currentBalance}
                                                        value={showInvoicePaymentPrice(invoice.invoiceNumber)}
                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                            upateInvoiceAmount(
                                                                event,
                                                                invoice.invoiceNumber,
                                                                invoice.currentBalance,
                                                            )
                                                        }
                                                        onBlur={() => showInvoicePaymentPriceInfo()}
                                                    />
                                                    <Typography {...styles.toolTip} className="tooltip center-align">
                                                        {translate(
                                                            "Please enter the amount you would like to pay towards this invoice",
                                                        )}
                                                    </Typography>
                                                </StyledWrapper>
                                            ) : Number(invoice.currentBalance) >= 0 ? (
                                                invoice.currentBalanceDisplay
                                            ) : (
                                                `${invoice.currentBalanceDisplay.substring(1)}`
                                            )}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            {...styles.actionColumnBody}
                                            className={
                                                getInvoiceParams.invoiceType === "0"
                                                    ? "disabled viewinvoicecontentactionbtn"
                                                    : "action-btn viewinvoicecontentactionbtn"
                                            }
                                        >
                                            {showInvoicePaymentPrice(invoice.invoiceNumber) > invoice.currentBalance &&
                                                invoice.properties?.isDuplicateInvoicePayment !== "true" ? (
                                                <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                    <StyledWrapper>
                                                        <ErrorCrossIcon />
                                                    </StyledWrapper>
                                                    <Typography {...styles.selectedInvoiceInfoMsg}>
                                                        {translate("Amount cannot be more than balance")}
                                                    </Typography>
                                                    <StyledWrapper
                                                        onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                    >
                                                        <CrossIcon />
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            ) : checkifSelected(invoice.invoiceNumber) &&
                                                invoice.properties?.isDuplicateInvoicePayment !== "true" &&
                                                showInvoicePaymentPrice(invoice.invoiceNumber) < 0 ? (
                                                <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                    <Typography>
                                                        {translate("You will apply this amount toward your total.")}
                                                    </Typography>
                                                    <StyledWrapper
                                                        onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                    >
                                                        <CrossIcon />
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            ) : showInvoicePaymentPrice(invoice.invoiceNumber) === 0 &&
                                                invoice.properties?.isDuplicateInvoicePayment !== "true" ? (
                                                <StyledWrapper {...styles.selectedInvoiceMsgContainer}>
                                                    <StyledWrapper>
                                                        <ErrorCrossIcon />
                                                    </StyledWrapper>
                                                    <Typography>{translate("Amount cannot be 0")}</Typography>
                                                    <StyledWrapper
                                                        onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                    >
                                                        <CrossIcon />
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            ) : checkifSelected(invoice.invoiceNumber) &&
                                                invoice.properties?.isDuplicateInvoicePayment !== "true" ? (
                                                <StyledWrapper
                                                    {...styles.selectedInvoiceMsgContainer}
                                                    ref={dropdownRef}
                                                >
                                                    <StyledWrapper
                                                        {...styles.dropdownContainer}
                                                        className={
                                                            showInvoicePaymentPrice(invoice.invoiceNumber) ===
                                                                invoice.currentBalance
                                                                ? "disabled"
                                                                : "normal"
                                                        }
                                                    >
                                                        <StyledWrapper
                                                            {...styles.dropdownValue}
                                                            onClick={() => handleDropdownToggle(invoice.invoiceNumber)}
                                                            className={
                                                                openDropdowns[invoice.invoiceNumber] ? "open" : "closed"
                                                            }
                                                        >
                                                            {reasonCodes
                                                                .find(
                                                                    code =>
                                                                        code.ReasonCode ===
                                                                        displayReasonCode(invoice.invoiceNumber),
                                                                )
                                                                ?.ReasonCodeDescription.toLowerCase() || "Reason"}

                                                            <Typography {...styles.icon}>
                                                                <ChevronDown />
                                                            </Typography>
                                                        </StyledWrapper>
                                                        {openDropdowns[invoice.invoiceNumber] ? (
                                                            <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                                                <StyledWrapper
                                                                    {...styles.dropdownValues}
                                                                    as={"li"}
                                                                    onClick={() =>
                                                                        handleReasonCode("00", invoice.invoiceNumber)
                                                                    }
                                                                >
                                                                    {translate("Reason")}
                                                                </StyledWrapper>
                                                                {reasonCodes.length
                                                                    ? reasonCodes.map(codes => {
                                                                        return (
                                                                            <StyledWrapper
                                                                                {...styles.dropdownValues}
                                                                                as={"li"}
                                                                                onClick={() =>
                                                                                    handleReasonCode(
                                                                                        codes.ReasonCode,
                                                                                        invoice.invoiceNumber,
                                                                                    )
                                                                                }
                                                                                className={
                                                                                    reasonCodes
                                                                                        .find(
                                                                                            code =>
                                                                                                code.ReasonCode ===
                                                                                                displayReasonCode(
                                                                                                    invoice.invoiceNumber,
                                                                                                ),
                                                                                        )
                                                                                        ?.ReasonCodeDescription.toLowerCase() ===
                                                                                        codes.ReasonCodeDescription.toLowerCase()
                                                                                        ? "active"
                                                                                        : " normal"
                                                                                }
                                                                            >
                                                                                {codes.ReasonCodeDescription.toLowerCase()}
                                                                            </StyledWrapper>
                                                                        );
                                                                    })
                                                                    : null}
                                                            </StyledWrapper>
                                                        ) : null}
                                                    </StyledWrapper>
                                                    <StyledWrapper
                                                        onClick={() => onCheckboxChange(invoice.invoiceNumber)}
                                                    >
                                                        <CrossIcon />
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            ) : getInvoiceParams.invoiceType === "1" ? (
                                                showPayButton(invoice) &&
                                                    invoice.currentBalance > 0 &&
                                                    invoice.properties?.isDuplicateInvoicePayment === undefined ? (
                                                    <span onClick={() => onCheckboxChange(invoice.invoiceNumber)}>
                                                        {translate("Pay")}
                                                    </span>
                                                ) : showApplyButton(invoice) &&
                                                    invoice.properties?.isDuplicateInvoicePayment === undefined ? (
                                                    <span onClick={() => onCheckboxChange(invoice.invoiceNumber)}>
                                                        {translate("Apply")}
                                                    </span>
                                                ) : (
                                                    invoice.properties?.isDuplicateInvoicePayment === "true" && (
                                                        <span style={{ cursor: "text" }}>
                                                            {translate("Payment Pending")}
                                                        </span>
                                                    )
                                                )
                                            ) : getInvoiceParams.invoiceType === "0" &&
                                                customerSequenceMatchesBillTo &&
                                                invoice.properties?.isDuplicateInvoicePayment === undefined ? (
                                                <span> {translate("Pay")} </span>
                                            ) : invoice.properties?.isDuplicateInvoicePayment === "true" ? (
                                                <span>{translate("Payment Pending")}</span>
                                            ) : null}
                                        </StyledWrapper>
                                    </GridItem>
                                    // </StyledWrapper>
                                );
                            })}
                        </GridItem>
                    </GridContainer>
                    {finalinvoicesToPay.length && selectedCount && getInvoiceParams.invoiceType !== "0" ? (
                        <>
                            {
                                <StyledWrapper {...styles.paymentTotal}>
                                    <Typography>
                                        {/* // {translate("You have selected {0} invoice.", `${finalinvoicesToPay.length}`)} */}
                                        {translate(
                                            `You have selected ${finalinvoicesToPay.length -
                                            finalinvoicesToPay.filter(
                                                fv => fv.properties.isDuplicateInvoicePayment === "true",
                                            ).length
                                            } invoice${finalinvoicesToPay.length > 1 ? "s" : ""}.`,
                                        )}
                                    </Typography>

                                    <StyledWrapper>
                                        <StyledWrapper {...styles.paymentInfoContainer}>
                                            <Typography {...styles.paymentTitle}>
                                                {translate("Payment Total:")}
                                                <Typography {...styles.payTotal}>
                                                    {allInvoicesTotal(finalinvoicesToPay) >= 0
                                                        ? `$${allInvoicesTotal(
                                                            finalinvoicesToPay.filter(
                                                                fv =>
                                                                    fv.properties.isDuplicateInvoicePayment !==
                                                                    "true",
                                                            ),
                                                        ).toFixed(2)}`
                                                        : `($${allInvoicesTotal(
                                                            finalinvoicesToPay.filter(
                                                                fv =>
                                                                    fv.properties.isDuplicateInvoicePayment !==
                                                                    "true",
                                                            ),
                                                        )
                                                            .toFixed(2)
                                                            .substring(1)})`}
                                                </Typography>
                                            </Typography>
                                            <Button
                                                {...styles.paymentBtn}
                                                disabled={
                                                    allInvoicesTotal(finalinvoicesToPay) < 0 ||
                                                    showAllowInvoicePayment == false
                                                }
                                                onClick={updatePaymentHandler}
                                            >
                                                {translate("Payment Recap")}
                                            </Button>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            }
                            {allInvoicesTotal(finalinvoicesToPay) < 0 ? (
                                <StyledWrapper {...styles.warningInfoContainer}>
                                    <StyledWrapper {...styles.warningInfoIcon}>
                                        <WarningIcon />
                                    </StyledWrapper>
                                    <Typography {...styles.warningInfoText}>
                                        {translate(
                                            "To redeem a credit memo, the total value of the invoice must be greater than or equal to the value of the credit memo.",
                                        )}
                                    </Typography>
                                </StyledWrapper>
                            ) : null}
                        </>
                    ) : null}
                </>
            )}
            <ADIInvoiceHistoryImportModal
                isOpen={isImportModalOpen}
                closeQuoteModal={() => {
                    setIsImportModalOpen(false);
                    setInvalidMessage([]);
                }}
                invalidMessage={invalidMessage}
                formErrors={formErrors}
                handleDrop={handleDrop}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDragStart={handleDragStart}
                onAddFile={onAddFile}
                files={files}
                onRemoveFile={onRemoveFile}
                setDropzone={getDragZone}
                handleImportAndReview={updatePaymentHandler}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ADIInvoiceHistoryTable)),
    definition: {
        group: "Invoice History",
        displayName: "ADI Invoice History Table",
        allowedContexts: [InvoiceHistoryPageContext],
        fieldDefinitions: [
            {
                name: fields.showInvoiceNumber,
                displayName: "Invoice #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.showInvoiceDate,
                displayName: "Invoice Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showTerms,
                displayName: "Terms",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showDueDate,
                displayName: "Due Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showPONumber,
                displayName: "Order #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showInvoiceTotal,
                displayName: "Invoice Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showCurrentBalance,
                displayName: "Current Balance",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;
